import { createSlice } from '@reduxjs/toolkit';
import * as Sentry from '@sentry/react';
// utils
import { shortAudienceReason } from '../../components/whatsappComponents/audienceRulesUtils';
import { callApi } from '../../utils/apiCall';
import { hasFeatureAccess } from '../../utils/featurePermission';
//
import { dispatch, store } from '../store';
import { updateError } from './user';
import {
    UNCONFIRMED_SEND_MESSAGE,
    isUnconfirmedSendOutcome,
    newClientMessageId,
    sendRefusalReason,
    shouldRecheckSend,
} from '../../utils/whatsappSendSafety';
import { upsertWidgetCounter } from './widgetCounter';
import { fetchContactDetails } from '../../pages/whatsapp/whatsappUtil';
import {
    getConnectedWhatsappBusinessAccounts,
    getHistoricalWhatsappBusinessAccounts,
    isConnectedWhatsappBusinessAccount,
    normalizeWhatsappBusinessAccounts,
} from '../../utils/whatsappAccountLifecycle';

// 2026-05-06 P0 — held-Enter fired sendWhatsappMessage twice ~18 ms apart,
// before whatsppSendLoading flipped, so two outbounds hit Meta and two
// WhatsappMessage rows landed. The synchronous Set is the *first* gate
// (closes before any await); the BE accepts the same UUID for 60s and
// returns the original message on a replay. Either layer alone leaks —
// the FE latch is too eager (e.g. component remount), the BE alone still
// burns network.
const waSendInflightKeys = new Set();

const captureWhatsappWorkflow = (eventName, { level = 'info', tags = {}, extra = {} } = {}) => {
    try {
        const sanitizedExtra = {
            practiceId: extra.practiceId ?? undefined,
            businessId: extra.businessId ?? undefined,
            phoneId: extra.phoneId ?? undefined,
            threadId: extra.threadId ?? undefined,
            threadCount: extra.threadCount ?? undefined,
            selectedCount: extra.selectedCount ?? undefined,
            succeededCount: extra.succeededCount ?? undefined,
            failedCount: extra.failedCount ?? undefined,
            status: extra.status ?? undefined,
            reason: extra.reason ?? undefined,
            errorMessage: extra.errorMessage ?? undefined,
        };
        Sentry.addBreadcrumb({
            category: 'whatsapp.workflow',
            message: eventName,
            level,
            data: sanitizedExtra,
        });
        if (level === 'warning' || level === 'error') {
            Sentry.captureMessage(`WhatsApp workflow: ${eventName}`, {
                level,
                tags: {
                    feature: 'whatsapp-workflow',
                    whatsapp_event: eventName,
                    ...tags,
                },
                extra: sanitizedExtra,
            });
        }
    } catch (_) {
        // Workflow telemetry must never block the user action.
    }
};

// practiceTreatmentList's updatePracticeId (dispatched by switchPractice).
// Matched by type string: importing that slice here would pull the task
// slices into every whatsappV2 consumer and test.
export const PRACTICE_ID_CHANGED = 'practiceTreatmentList/updatePracticeId';

const hasValidWhatsappContext = (businessId, phoneId) => (
    Boolean(businessId)
    && businessId !== -1
    && String(businessId) !== '-1'
    && Boolean(phoneId)
    && phoneId !== -1
    && String(phoneId) !== '-1'
);

export const hasConnectedWhatsappWriteContext = (state) => (
    state?.currentBusinessIsConnected === true
    && hasValidWhatsappContext(state?.currentBusinessId, state?.currentWhatsappPhoneId)
);

// 2026-06-11 P0 — initializeWhatsapp() has no mutex. Two concurrent calls
// can race (e.g. DashboardLayout fires init#1 for practice 19 on mount;
// Whatsapp.jsx detects URL practiceId=36 and dispatches switchPractice(36)
// which triggers DashboardLayout's [currentPractice] effect to fire init#2
// for practice 36). If init#1 completes LAST it stamps
// initializedForPracticeId=19, leaving the UI in a permanent skeleton with
// 19≠36. The generation counter below makes every concurrent call
// self-cancelling: the latest dispatch owns the current generation; any
// older call checks waInitGeneration !== myGen before stamping and silently
// returns without touching Redux state.
let waInitGeneration = 0;
const waTemplateRefreshGenerations = new Map();

const templateRefreshKey = (practiceId, businessId) => `${practiceId}:${businessId}`;

const beginTemplateRefresh = (practiceId, businessId) => {
    const key = templateRefreshKey(practiceId, businessId);
    const generation = (waTemplateRefreshGenerations.get(key) || 0) + 1;
    waTemplateRefreshGenerations.set(key, generation);
    return generation;
};

const isLatestTemplateRefresh = (practiceId, businessId, generation) => (
    waTemplateRefreshGenerations.get(templateRefreshKey(practiceId, businessId)) === generation
);
const WHATSAPP_CALL_TERMINAL_STATUSES = new Set(['rejected', 'terminated', 'failed']);
const WHATSAPP_CALL_ICON_VISIBILITY_META_VALUES = Object.freeze({
    visible: 'DEFAULT',
    hidden: 'DISABLE_ALL',
    DEFAULT: 'DEFAULT',
    DISABLE_ALL: 'DISABLE_ALL',
});

export const normalizeWhatsappCallingSettingsPayload = (payload) => {
    if (!payload || typeof payload !== 'object' || Array.isArray(payload)) return payload;
    const normalized = { ...payload };
    if (Object.prototype.hasOwnProperty.call(normalized, 'call_icon_visibility')) {
        const visibility = normalized.call_icon_visibility;
        normalized.call_icon_visibility = visibility == null
            ? null
            : WHATSAPP_CALL_ICON_VISIBILITY_META_VALUES[visibility] || visibility;
    }
    return normalized;
};

export const WHATSAPP_FILTERS = Object.freeze({
    "ALL_FILTER": "ALL_FILTER",
    "UNREAD_FILTER": "UNREAD_FILTER",
    "PENDING_FILTER": "PENDING_FILTER",
    "NEW_LEAD_FILTER": "NEW_LEAD_FILTER",
    "ARCHIVED_FILTER": "ARCHIVED_FILTER",
    "CAMPAIGN_FILTER": "CAMPAIGN_FILTER"
});

const SEARCH_MODE_PARAMS = Object.freeze({
    // 2026-06-11 BUG FIX — 'All' tab was hiding answered non-campaign threads.
    // campaign_or_pending=true was keeping ONLY campaign or pending threads,
    // so any conversation where the last message is an outbound reply vanished.
    // 'All' must mean all open, non-archived conversations — nothing more.
    '': '&is_closed=false&lead__is_archived=false',
    // F1 (2026-07-01) — direction-based Needs Reply. `needs_reply=true` means the
    // patient sent the last message OR there is an unanswered missed call; it is
    // cleared only by an outbound reply or Resolve, never by reading the thread.
    // `needs_reply=false` = practice sent the last message AND at least one
    // outbound exists ("Waiting on patient"). Threads with no outbound ever
    // appear in NEITHER tab. `is_unread` remains for the visual unread dot only;
    // `is_pending` is deprecated on the BE and must not be sent.
    // #34 (2026-07-02) — HOTFIX: like the 'All' tab, the direction tabs must
    // exclude archived-lead threads. Without lead__is_archived=false the list
    // request's base is_archived_included=true pulled archived-lead threads
    // into these tabs (prod practice 11: PENDING 2384 vs the real 135).
    'FOLLOW_UP': '&is_closed=false&needs_reply=true&lead__is_archived=false',
    'PENDING': '&is_closed=false&needs_reply=false&lead__is_archived=false',
    'TAG': '&is_closed=false',
    'MY': '&is_closed=false',
    'CLOSED': '&is_closed=true',
    'ARCHIVED': '&lead__is_archived=true',
    'CAMPAIGN': '&is_closed=false&campaign__isnull=false',
});
// ---------------------------------------------------------------------
const initialState = {
    // loading states
    isLoading: false,
    threadLoading: false,
    threadNextLoading: false,
    whatsappFiltersLoading: false,
    whatsappSearchLeadLoading: false,
    whatsappChatScreenLoading: false,
    whatsappChatNextAPILoading: false,
    whatsppSendLoading: false,
    // current values
    currentBusinessId: -1,
    currentWhatsappPhoneId: -1,
    currentFilter: WHATSAPP_FILTERS.ALL_FILTER,
    currentSearch: "",
    threadMessages: [],
    threadNextAPI: "",
    filterCounts: {},
    currentWhatsappTemplate: [],
    whatsappChats: [],
    whatsAppChatNextAPI: "",
    whatsAppChatMetaData: {},
    // total value maps
    whatsappJwtTokens: {},
    whatsppTemplates: {},
    templateMapPracticeId: null,
    businessIdToPhoneMap: {},
    businessAccountConnectionMap: {},
    historicalBusinessAccounts: [],
    currentBusinessIsConnected: false,
    // search results
    leadSearchResult: [],
    pieSocketSubscriptions: [],
    // not configured (no WhatsApp Business setup for this practice)
    whatsappNotConfigured: false,
    // true when initializeWhatsapp exhausted retries on a real (non-config) error
    whatsappInitError: false,
    // practiceId that currentBusinessId / currentWhatsappPhoneId were initialised
    // for. Used by getWhatsaAppThreads / fetchWhatsappChatAndOtherMetaData to
    // detect a stale cross-practice state (practiceId switched but WA IDs not yet
    // refreshed) and trigger re-initialisation instead of firing a mixed URL.
    initializedForPracticeId: null,
    // practiceId the SELECTED account/phone (currentBusinessId /
    // currentWhatsappPhoneId) belong to. Unlike initializedForPracticeId it
    // survives changeCurrentPhoneAndBusiness (a phone switch stays inside the
    // practice), so a practice switch can always tell the selection is stale.
    // JAVASCRIPT-REACT-1HW: see PRACTICE_ID_CHANGED below.
    selectionPracticeId: null,
    // notif count
    whatsappNotifCount: 0,
    // searchMode
    searchMode: "",
    currentTag: "",
    currentUser: null,
    practiceChoices: [],
    allowedUsageTypes: null, // null = show all (backward compatible), array = filter by usage_type
    allowedEmailUsageTypes: null, // null = show all, array = filter email templates by usage_type
    // AI Configuration (legacy — used by CallScriptV2 AI assistant, loaded from practice choices)
    aiConfig: null,
    aiConfigLoading: false,
    // AI Wizard Configuration (loaded from /ai-config/ endpoint, separate from legacy aiConfig)
    aiWizardConfig: null,
    aiWizardConfigLoading: false,
    aiWizardStatus: null, // { wizard_enabled: bool, wizard_completed: bool }
    // AI Assistant
    aiAssistantLoading: false,
    aiAssistantResult: null,
    aiAssistantError: null,
    aiAssistantActionType: null,
    // Service-window-closed dialog payload. Set when the backend rejects a
    // free-form send with HTTP 409 (PMP rules — Meta won't deliver outside
    // the 24h window). Cleared by ChatPage when the operator dismisses the
    // dialog or switches to a template.
    serviceWindowClosedDialog: null,
    // Track C / F8 Message Info — keyed by message id. Populated lazily by
    // fetchMessageInfo() when the operator opens the Info drawer. Shape:
    //   { [messageId]: { sent_at, delivered_at, read_at, failed_at } | null }
    // null means the fetch is in flight (loading); undefined means not yet
    // requested.
    messageInfo: {},
    messageInfoLoading: {},
    callingSettings: null,
    callingSettingsLoading: false,
    callingSettingsError: null,
    whatsappCallsById: {},
    whatsappCallRecordingsByCallId: {},
    whatsappCallTranscriptByCallId: {},
    whatsappActiveCallId: null,
    whatsappCallActionLoading: false,
    whatsappCallError: null,
    // F9 — call events rendered inline in the conversation timeline.
    // threadCallEvents holds the calls/?thread={id} rows for the OPEN thread
    // only; threadCallEventsThreadId records which thread they belong to so
    // ChatPage can ignore stale events while switching conversations.
    threadCallEvents: [],
    threadCallEventsThreadId: null,
};

const slice = createSlice({
    name: 'whatsApp',
    initialState,
    reducers: {
        // For Reseting the reducer
        reset(state) {
            Object.assign(state, initialState);
        },
        startInitializeWhatsapp(state) {
            // Preserve PieSocket subscriptions across the reset so that
            // initializeWhatsapp() can read them (at line ~741, before the
            // internal startInitializeWhatsapp dispatch) and call
            // unsubscribeConnections() to clean up old-practice listeners.
            // Without this, Object.assign wipes pieSocketSubscriptions to []
            // first, unsubscribeConnections gets an empty array, and
            // old-practice socket listeners remain alive — leaking patient
            // names/message bodies as toasts while viewing another practice.
            const subs = state.pieSocketSubscriptions;
            Object.assign(state, initialState);
            state.isLoading = true;
            state.pieSocketSubscriptions = subs;
        },
        completeWhatsappInitialization(state, action) {
            const { payload } = action ?? {};
            state.businessIdToPhoneMap = payload?.businessIdToPhoneMap ?? {};
            state.businessAccountConnectionMap = payload?.businessAccountConnectionMap ?? {};
            state.historicalBusinessAccounts = payload?.historicalBusinessAccounts ?? [];
            state.currentBusinessId = payload?.currentBusinessId ?? -1;
            state.currentWhatsappPhoneId = payload?.currentPhoneid ?? -1;
            state.currentBusinessIsConnected = payload?.currentBusinessIsConnected === true;
            state.whatsappJwtTokens = payload?.jwtTokens ?? {};
            state.whatsppTemplates = payload?.templates ?? {};
            state.templateMapPracticeId = payload?.practiceId ?? null;
            state.isLoading = false;
            state.currentWhatsappTemplate = payload?.currentWhatsappTemplate ?? [];
            state.practiceChoices = payload?.practiceChoices ?? [];
            state.allowedUsageTypes = payload?.allowedUsageTypes ?? null;
            state.allowedEmailUsageTypes = payload?.allowedEmailUsageTypes ?? null;
            state.aiConfig = payload?.aiConfig ?? null;
            // Stamp which practice these WA IDs belong to. Both the happy-path
            // and the noConfig path include practiceId in the payload so this is
            // always practice-scoped after init.
            state.initializedForPracticeId = payload?.practiceId ?? null;
            state.selectionPracticeId = payload?.practiceId ?? null;
            // Clear old-practice subs: startInitializeWhatsapp preserved them so
            // initializeWhatsapp() could read+unsubscribe them. By this point they
            // are disconnected, so clearing here prevents updateSubChannel (called
            // later by the layout) from appending new subs onto stale entries.
            state.pieSocketSubscriptions = [];
            // Mark as not configured only when the API explicitly returned no business account.
            // When payload is empty (error fallback), leave whatsappNotConfigured unchanged
            // so transient errors don't falsely show the "not configured" empty state.
            if (payload && 'currentBusinessId' in payload) {
                state.whatsappNotConfigured = payload.currentBusinessId === -1;
            }
            // Proactively flip threadLoading so the skeleton stays visible across
            // the init→thread-fetch gap. Contacts' useEffect fires *after* the render
            // triggered by currentBusinessId changing, leaving a render where
            // isLoading=false + threadLoading=false + threadMessages=[] → "No
            // conversations found" flash. Setting threadLoading=true here bridges
            // that gap; completeFetchWhatsappThreads clears it.
            // Excluded for the noConfig path (currentBusinessId=-1) — Whatsapp.jsx
            // renders <WhatsAppEmptyState> before Contacts mounts in that case.
            if ((payload?.currentBusinessId ?? -1) !== -1) {
                state.threadLoading = true;
            }
        },
        cancelInitializeWhatsapp(state) {
            // Releases the loading flag and NOTHING else.
            //
            // 2026-10-08 BUG — startInitializeWhatsapp() sets isLoading=true, and
            // initializeWhatsapp() has early returns AFTER that flip. The two
            // generation guards are safe (a newer init owns the flag and will
            // clear it), but the practice-scope guards are not: when they bail,
            // no init is pending and isLoading stays true forever.
            //
            // That is an absorbing state, because EVERY recovery branch in
            // getWhatsaAppThreads and fetchWhatsappChatAndOtherMetaData is gated
            // on !isLoading — so once wedged, nothing can dispatch a new init.
            // whatsappV2 is also exempt from PRACTICE_SWITCH_RESET, so switching
            // practice does not clear it either. Only a full reload did.
            // Symptom: "Failed to get Whatsapp Messages" on every visit.
            //
            // Deliberately NOT failWhatsappInitialization() here: that also sets
            // whatsappInitError, and a practice-scope bail is a correct, expected
            // outcome, not an error worth a retry toast.
            state.isLoading = false;
        },
        failWhatsappInitialization(state) {
            state.isLoading = false;
            state.whatsappInitError = true;
            // Intentionally DO NOT touch currentBusinessId/currentWhatsappPhoneId or
            // whatsappNotConfigured — a transient failure must not look like "no config".
            // Explicitly null the practiceId stamp so the next successful init
            // re-stamps it (functionally redundant — startInitializeWhatsapp already
            // resets it — but makes the failed state explicit in the machine).
            state.initializedForPracticeId = null;
        },
        setAiConfig(state, action) {
            state.aiConfig = action.payload;
        },
        setAiConfigLoading(state, action) {
            state.aiConfigLoading = action.payload;
        },
        setAiWizardConfig(state, action) {
            state.aiWizardConfig = action.payload;
        },
        setAiWizardConfigLoading(state, action) {
            state.aiWizardConfigLoading = action.payload;
        },
        setAiWizardStatus(state, action) {
            state.aiWizardStatus = action.payload;
        },
        setTemplateFilterConfigs(state, action) {
            const { allowedUsageTypes, allowedEmailUsageTypes } = action.payload ?? {};
            state.allowedUsageTypes = allowedUsageTypes ?? null;
            state.allowedEmailUsageTypes = allowedEmailUsageTypes ?? null;
        },
        startFetchWhatsappThreads(state) {
            state.threadLoading = true;
        },
        completeFetchWhatsappThreads(state, action) {
            const { payload } = action ?? {}
            state.threadLoading = false;
            state.threadMessages = payload?.threadMessages ?? [];
            state.threadNextAPI = payload?.threadNext ?? "";
            state.isLoading = false;
        },
        startFetchWhatsappFilterCount(state) {
            state.whatsappFiltersLoading = true;
        },
        completeFetchWhatsappFilterCount(state, action) {
            const { payload } = action ?? {};
            state.whatsappFiltersLoading = false;
            state.filterCounts = payload?.filterCounts ?? {};
            state.isLoading = false;
        },
        startThreadNextFetch(state) {
            state.threadNextLoading = true;
        },
        completeThreadNextFetch(state, action) {
            const { payload } = action ?? {}
            state.threadNextLoading = false;
            state.threadMessages = payload?.threadMessages ?? state.threadMessages;
            state.threadNextAPI = payload?.threadNext ?? state.threadNext;
            state.isLoading = false;
        },
        changeCurrentFilter(state, action) {
            const { payload } = action ?? {}
            state.currentFilter = payload?.changedFilter;
            state.threadLoading = true;
        },
        changeCurrentSearch(state, action) {
            const { payload } = action ?? {}
            state.currentSearch = payload?.changedSearch
        },
        changeCurrentPhoneAndBusiness(state, action) {
            const { payload } = action ?? {}
            state.currentBusinessId = payload?.currentBusinessId ?? -1;
            state.currentWhatsappPhoneId = payload?.currentWhatsappPhoneId ?? -1;
            state.currentBusinessIsConnected = (
                state.businessAccountConnectionMap?.[String(payload?.currentBusinessId)] === true
            );
            state.currentWhatsappTemplate = payload?.currentWhatsappTemplate;
            state.currentFilter = WHATSAPP_FILTERS.ALL_FILTER;
            state.currentSearch = "";
            state.threadMessages = [];
            state.threadNextAPI = "";
            state.filterCounts = {};
            state.whatsappChats = [];
            state.whatsAppChatNextAPI = "";
            state.whatsAppChatMetaData = {};
            // Reset so the stale-practice guard doesn't treat the newly-selected
            // phone/business as pre-validated for the old practice. Safe today
            // (ProfileSwitcher only switches within the same practice), but
            // defensive against future cross-practice phone switching.
            state.initializedForPracticeId = null;
        },
        saveSyncedTemplates(state, action) {
            const { payload } = action ?? {};
            const businessId = String(payload?.businessId ?? '');
            if (!businessId || String(payload?.practiceId) !== String(state.templateMapPracticeId)) return;
            const templates = Array.isArray(payload?.templates) ? payload.templates : [];
            state.whatsppTemplates = {
                ...state.whatsppTemplates,
                [businessId]: templates,
            };
            if (String(state.currentBusinessId) === businessId) {
                state.currentWhatsappTemplate = templates;
            }
        },
        updateCurrentTemplateFavourite(state, action) {
            const { id, isFavourite } = action.payload ?? {};
            if (!Array.isArray(state.currentWhatsappTemplate)) return;
            const templateId = String(id);
            state.currentWhatsappTemplate = state.currentWhatsappTemplate.map((template) =>
                String(template.id) === templateId ? { ...template, is_favourite: isFavourite } : template
            );
        },
        startLeadSearchResults(state) {
            state.whatsappSearchLeadLoading = true;
            state.leadSearchResult = [];
        },
        storeLeadSearchResults(state, action) {
            const { payload } = action ?? {};
            state.leadSearchResult = payload?.data;
            state.whatsappSearchLeadLoading = false;
        },
        saveWhatsappChatMetaData(state, action) {
            const { payload } = action ?? {};
            state.whatsAppChatMetaData = payload;
        },
        startWhatsappChatLoad(state) {
            state.whatsappChatScreenLoading = true
        },
        completeWhatsapChatLoad(state, action) {
            const { payload } = action ?? {};
            state.whatsappChatScreenLoading = false;
            state.whatsappChats = payload?.whatsappChats ?? [];
            state.whatsAppChatMetaData = payload?.whatsAppChatMetaData ?? {};
            state.whatsAppChatNextAPI = payload?.whatsAppChatNextAPI ?? ""
        },
        refreshTagInfo(state, action) {
            const { payload } = action ?? {};
            state.whatsAppChatMetaData = payload?.whatsAppChatMetaData ?? {};
        },
        errorWhatsappChatLoad(state) {
            state.whatsappChatScreenLoading = false;
            state.whatsappChats = [];
            state.whatsAppChatNextAPI = "";
            state.whatsAppChatMetaData = {};
        },
        resetWhatsappChat(state) {
            state.whatsappChatScreenLoading = false;
            state.whatsappChats = [];
            state.whatsAppChatNextAPI = "";
            state.whatsAppChatMetaData = {};
        },
        startWhatsappNextMessageFetch(state) {
            state.whatsappChatNextAPILoading = true
        },
        completeWhatsappNextMessageFetch(state, action) {
            const { payload } = action ?? {};
            state.whatsappChatNextAPILoading = false;
            state.whatsappChats = payload?.whatsappChats;
            state.whatsAppChatNextAPI = payload?.whatsAppChatNextAPI;
        },
        toggleWhatsappSendLoading(state, action) {
            state.whatsppSendLoading = action?.payload ?? false;
        },
        showServiceWindowClosedDialog(state, action) {
            state.serviceWindowClosedDialog = action?.payload ?? null;
        },
        clearServiceWindowClosedDialog(state) {
            state.serviceWindowClosedDialog = null;
        },
        saveLeadDetails(state, action) {
            const { payload } = action ?? {};
            state.whatsAppChatMetaData = payload ?? {};
        },
        saveThreadDetails(state, action) {
            const { payload } = action ?? {};
            state.threadMessages = payload ?? {};
        },
        saveMessageDetails(state, action) {
            const { payload } = action ?? {};
            // `whatsappChats` is always an array (see initialState + every other
            // reducer here) — `?? {}` was the wrong-shape fallback that let a
            // falsy payload turn it into an object, which is exactly the class of
            // bug BookingPanel's `whatsappChats?.[phone]` lookup silently masked.
            // All 3 call sites already pass `results ?? []`, so this is currently
            // unreachable in practice, but keep the field's invariant intact.
            state.whatsappChats = payload ?? [];
        },
        updatePieSocketSubscriptions(state, action) {
            state.pieSocketSubscriptions = action?.payload ?? []
        },
        addWhatsappNotifCount(state, action) {
            state.whatsappNotifCount = action?.payload ?? 0
        },
        updateSearchMode(state, action) {
            state.searchMode = action?.payload?.mode;
            state.currentUser = action?.payload?.user;
        },
        updateSearchTag(state, action) {
            state.currentTag = action?.payload
        },
        // AI Assistant reducers
        startAiAssistant(state, action) {
            state.aiAssistantLoading = true;
            state.aiAssistantError = null;
            state.aiAssistantResult = null;
            state.aiAssistantActionType = action?.payload ?? null;
        },
        completeAiAssistant(state, action) {
            state.aiAssistantLoading = false;
            state.aiAssistantResult = action?.payload;
        },
        failAiAssistant(state, action) {
            state.aiAssistantLoading = false;
            state.aiAssistantError = action?.payload;
        },
        clearAiAssistant(state) {
            state.aiAssistantLoading = false;
            state.aiAssistantResult = null;
            state.aiAssistantError = null;
            state.aiAssistantActionType = null;
        },
        removeThreadFromList(state, action) {
            const threadId = action.payload;
            state.threadMessages = (state.threadMessages ?? []).filter(
                (t) => String(t?.id ?? t?.thread_id) !== String(threadId)
            );
        },
        // Bulk variant — same filter logic but accepts an array of ids.
        // Used by `bulkArchiveThreads` to optimistically remove all
        // succeeded threads from the visible list in a single render.
        removeThreadsFromList(state, action) {
            const ids = Array.isArray(action.payload) ? action.payload : [];
            if (ids.length === 0) return;
            const idSet = new Set(ids.map((x) => String(x)));
            state.threadMessages = (state.threadMessages ?? []).filter(
                (t) => !idSet.has(String(t?.id ?? t?.thread_id))
            );
        },
        // Apply read state across many threads (bulk mark_as_read).
        // Mirrors the per-row optimistic update done by markAsRead's
        // refresh path: clear `unread_count` and flip `is_read=true`.
        applyThreadsRead(state, action) {
            const ids = Array.isArray(action.payload) ? action.payload : [];
            if (ids.length === 0) return;
            const idSet = new Set(ids.map((x) => String(x)));
            (state.threadMessages ?? []).forEach((t) => {
                if (t && idSet.has(String(t?.id ?? t?.thread_id))) {
                    t.is_read = true;
                    t.unread_count = 0;
                }
            });
        },
        // Inverse — flip `is_read=false` so the unread badge reappears.
        applyThreadsUnread(state, action) {
            const ids = Array.isArray(action.payload) ? action.payload : [];
            if (ids.length === 0) return;
            const idSet = new Set(ids.map((x) => String(x)));
            (state.threadMessages ?? []).forEach((t) => {
                if (t && idSet.has(String(t?.id ?? t?.thread_id))) {
                    t.is_read = false;
                }
            });
        },
        applyMessageReaction(state, action) {
            const { messageId, emoji, removed, byUser } = action.payload ?? {};
            if (messageId == null) return;
            const list = state.whatsappChats ?? [];
            const idx = list.findIndex((m) => String(m?.id) === String(messageId));
            if (idx === -1) return;
            const target = list[idx];
            if (removed || !emoji) {
                target.whatsapp_message_reaction = null;
            } else {
                target.whatsapp_message_reaction = {
                    ...(target.whatsapp_message_reaction || {}),
                    emoji,
                    is_outbound: true,
                    reacted_by: byUser ?? target.whatsapp_message_reaction?.reacted_by ?? null,
                };
            }
        },
        applyMessageRevoked(state, action) {
            const { messageId } = action.payload ?? {};
            if (messageId == null) return;
            const list = state.whatsappChats ?? [];
            const idx = list.findIndex((m) => String(m?.id) === String(messageId));
            if (idx === -1) return;
            list[idx].is_revoked = true;
            list[idx].body = '';
        },
        revertMessageRevoked(state, action) {
            const { messageId, body } = action.payload ?? {};
            if (messageId == null) return;
            const list = state.whatsappChats ?? [];
            const idx = list.findIndex((m) => String(m?.id) === String(messageId));
            if (idx === -1) return;
            list[idx].is_revoked = false;
            list[idx].body = body ?? '';
        },
        applyMessageStarred(state, action) {
            const { messageId, starredBy } = action.payload ?? {};
            if (messageId == null) return;
            const list = state.whatsappChats ?? [];
            const idx = list.findIndex((m) => String(m?.id) === String(messageId));
            if (idx === -1) return;
            list[idx].is_starred = true;
            if (starredBy) {
                list[idx].starred_by = starredBy;
            }
        },
        applyMessageUnstarred(state, action) {
            const { messageId } = action.payload ?? {};
            if (messageId == null) return;
            const list = state.whatsappChats ?? [];
            const idx = list.findIndex((m) => String(m?.id) === String(messageId));
            if (idx === -1) return;
            list[idx].is_starred = false;
            list[idx].starred_by = null;
        },
        applyMessagePinned(state, action) {
            const { messageId, threadId, pinnedAt, pinnedMessage } = action.payload ?? {};
            if (messageId == null && pinnedMessage == null) return;
            // Update the metadata for the currently-open thread only — other
            // threads' pins arrive on their own selectMeta load. The reducer
            // never touches threads that aren't visible.
            const meta = state.whatsAppChatMetaData ?? {};
            if (
                threadId != null
                && meta?.thread
                && String(meta.thread.id) === String(threadId)
            ) {
                meta.thread.pinned_message = pinnedMessage ?? meta.thread.pinned_message ?? { id: messageId };
                meta.thread.pinned_at = pinnedAt ?? meta.thread.pinned_at ?? null;
            }
        },
        applyMessageUnpinned(state, action) {
            const { threadId } = action.payload ?? {};
            const meta = state.whatsAppChatMetaData ?? {};
            if (
                threadId != null
                && meta?.thread
                && String(meta.thread.id) === String(threadId)
            ) {
                meta.thread.pinned_message = null;
                meta.thread.pinned_at = null;
            }
        },
        // Track C / F6: optimistic body swap when the operator hits Save on
        // the inline editor. Surgical — touches only the matching message and
        // never re-shapes the array, so memoised consumers downstream don't
        // re-render every bubble. Mirrors the Track A reaction reducer style.
        applyMessageEdited(state, action) {
            const { messageId, newBody, editedAt } = action.payload ?? {};
            if (!messageId) return;
            const idx = (state.whatsappChats ?? []).findIndex(
                (m) => `${m?.id}` === `${messageId}`,
            );
            if (idx === -1) return;
            const msg = state.whatsappChats[idx];
            // Prefer to mutate in place (createSlice + Immer makes this safe).
            // We DO NOT clear the previous body until the optimistic update
            // is reverted, because a Sentry'd 502 would otherwise blank the
            // bubble and confuse the operator mid-edit.
            state.whatsappChats[idx] = {
                ...msg,
                body: newBody,
                is_edited: true,
                edited_at: editedAt || msg?.edited_at,
            };
        },
        // Track C / F6: surgical revert when the editMessage thunk catches a
        // server failure. Restores the body the slice held BEFORE the
        // optimistic update; thunk is responsible for handing us the previous
        // body via payload.previousBody.
        revertMessageEdited(state, action) {
            const { messageId, previousBody, previousIsEdited, previousEditedAt } = action.payload ?? {};
            if (!messageId) return;
            const idx = (state.whatsappChats ?? []).findIndex(
                (m) => `${m?.id}` === `${messageId}`,
            );
            if (idx === -1) return;
            const msg = state.whatsappChats[idx];
            state.whatsappChats[idx] = {
                ...msg,
                body: previousBody ?? '',
                is_edited: previousIsEdited ?? msg?.is_edited ?? false,
                edited_at: previousEditedAt ?? msg?.edited_at ?? null,
            };
        },
        // Track C / F8: payloads from GET .../info/. Set messageInfo[id]=null
        // before the fetch (loading) and to the data afterwards.
        startMessageInfoFetch(state, action) {
            const messageId = action.payload;
            if (!messageId) return;
            state.messageInfoLoading = { ...(state.messageInfoLoading ?? {}), [messageId]: true };
        },
        completeMessageInfoFetch(state, action) {
            const { messageId, info } = action.payload ?? {};
            if (!messageId) return;
            state.messageInfo = { ...(state.messageInfo ?? {}), [messageId]: info };
            state.messageInfoLoading = { ...(state.messageInfoLoading ?? {}), [messageId]: false };
        },
        failMessageInfoFetch(state, action) {
            const messageId = action.payload;
            if (!messageId) return;
            state.messageInfoLoading = { ...(state.messageInfoLoading ?? {}), [messageId]: false };
        },
        startCallingSettingsFetch(state) {
            state.callingSettingsLoading = true;
            state.callingSettingsError = null;
        },
        completeCallingSettingsFetch(state, action) {
            state.callingSettingsLoading = false;
            state.callingSettings = action?.payload ?? null;
            state.callingSettingsError = null;
        },
        failCallingSettingsFetch(state, action) {
            state.callingSettingsLoading = false;
            state.callingSettingsError = action?.payload || 'Failed to load WhatsApp calling settings';
        },
        startWhatsappCallAction(state) {
            state.whatsappCallActionLoading = true;
            state.whatsappCallError = null;
        },
        completeWhatsappCallAction(state, action) {
            const call = action?.payload;
            state.whatsappCallActionLoading = false;
            state.whatsappCallError = null;
            if (!call?.id) return;
            state.whatsappCallsById = {
                ...(state.whatsappCallsById ?? {}),
                [call.id]: {
                    ...(state.whatsappCallsById?.[call.id] ?? {}),
                    ...call,
                },
            };
            if (WHATSAPP_CALL_TERMINAL_STATUSES.has(call?.status)) {
                if (String(state.whatsappActiveCallId) === String(call.id)) {
                    state.whatsappActiveCallId = null;
                }
                return;
            }
            state.whatsappActiveCallId = call.id;
        },
        failWhatsappCallAction(state, action) {
            state.whatsappCallActionLoading = false;
            state.whatsappCallError = action?.payload || 'WhatsApp call action failed';
        },
        /**
         * Live "the AI is working on this thread" status, keyed by thread.
         *
         * Transient by nature — a RESOLVED event clears the entry rather than
         * storing a terminal state, because the indicator's whole job is to say
         * something is in flight. A stale "AI is writing…" is worse than none.
         */
        receiveWhatsappAiStatus(state, action) {
            const data = action?.payload?.data ?? action?.payload;
            const threadId = data?.thread_id;
            if (threadId == null) return;
            const map = { ...(state.whatsappAiStatusByThread ?? {}) };
            if (data.state === 'resolved') {
                delete map[threadId];
            } else {
                map[threadId] = { ...data, received_at: Date.now() };
            }
            state.whatsappAiStatusByThread = map;
        },
        clearWhatsappAiStatus(state, action) {
            const threadId = action?.payload;
            if (threadId == null) return;
            const map = { ...(state.whatsappAiStatusByThread ?? {}) };
            delete map[threadId];
            state.whatsappAiStatusByThread = map;
        },
        receiveWhatsappCallUpdate(state, action) {
            const call = action?.payload?.data ?? action?.payload;
            if (!call?.id) return;
            const previous = state.whatsappCallsById?.[call.id] ?? {};
            const merged = { ...previous, ...call };
            state.whatsappCallsById = {
                ...(state.whatsappCallsById ?? {}),
                [call.id]: merged,
            };
            if (!WHATSAPP_CALL_TERMINAL_STATUSES.has(merged?.status)) {
                state.whatsappActiveCallId = call.id;
            } else if (String(state.whatsappActiveCallId) === String(call.id)) {
                state.whatsappActiveCallId = null;
            }
        },
        // F9 — thread-scoped call events for the inline chat timeline.
        setThreadCallEvents(state, action) {
            state.threadCallEvents = action?.payload?.calls ?? [];
            state.threadCallEventsThreadId = action?.payload?.threadId ?? null;
        },
        receiveWhatsappCallRecordingUpdate(state, action) {
            const recording = action?.payload?.data ?? action?.payload;
            if (!recording?.id || !recording?.call) return;
            const callId = String(recording.call);
            state.whatsappCallRecordingsByCallId = {
                ...(state.whatsappCallRecordingsByCallId ?? {}),
                [callId]: {
                    ...(state.whatsappCallRecordingsByCallId?.[callId] ?? {}),
                    [recording.id]: recording,
                },
            };
        },
        receiveWhatsappCallTranscriptSegment(state, action) {
            const segment = action?.payload?.data ?? action?.payload;
            if (!segment?.id || !segment?.call) return;
            const callId = String(segment.call);
            const existing = state.whatsappCallTranscriptByCallId?.[callId] ?? [];
            const next = existing.filter((item) => String(item.id) !== String(segment.id));
            next.push(segment);
            next.sort((a, b) => (
                (a.started_at_ms ?? 0) - (b.started_at_ms ?? 0)
                || String(a.created_at || '').localeCompare(String(b.created_at || ''))
            ));
            state.whatsappCallTranscriptByCallId = {
                ...(state.whatsappCallTranscriptByCallId ?? {}),
                [callId]: next,
            };
        },
        setWhatsappActiveCall(state, action) {
            state.whatsappActiveCallId = action?.payload ?? null;
        },
        clearWhatsappActiveCall(state) {
            state.whatsappActiveCallId = null;
            state.whatsappCallError = null;
        },
    },
    extraReducers: (builder) => {
        // JAVASCRIPT-REACT-1HW (2026-11-04): a user went from practice 18's
        // WhatsApp page to practice 16's. whatsappV2 is exempt from
        // PRACTICE_SWITCH_RESET and the re-init waits for the new practice's
        // permissions, so for that window the page held practice 18's account
        // and phone (38 / 75) under practice 16 and fired
        // GET /practices/16/whatsapp-business-accounts/38/whatsapp-phones/75/calls/
        // -> 404 -> "Something went wrong". Drop the old practice's selection in
        // the same dispatch that changes the practice id, so no render ever sees
        // the new id with the old account/phone. PieSocket handles and the
        // in-flight flags are left alone: initializeWhatsapp() still needs the
        // former to unsubscribe, and owns the latter.
        builder.addCase(PRACTICE_ID_CHANGED, (state, action) => {
            const selectionFor = state.selectionPracticeId;
            if (selectionFor == null || String(selectionFor) === String(action?.payload)) return;
            state.currentBusinessId = -1;
            state.currentWhatsappPhoneId = -1;
            state.currentBusinessIsConnected = false;
            state.currentWhatsappTemplate = [];
            state.businessIdToPhoneMap = {};
            state.businessAccountConnectionMap = {};
            state.historicalBusinessAccounts = [];
            state.whatsappJwtTokens = {};
            state.currentFilter = WHATSAPP_FILTERS.ALL_FILTER;
            state.currentSearch = "";
            state.threadMessages = [];
            state.threadNextAPI = "";
            state.filterCounts = {};
            state.whatsappChats = [];
            state.whatsAppChatNextAPI = "";
            state.whatsAppChatMetaData = {};
            state.callingSettings = null;
            state.whatsappNotConfigured = false;
            // Not initialised for the new practice: lets DashboardLayout's
            // init effect run, and every stale-practice guard bail.
            state.initializedForPracticeId = null;
            state.selectionPracticeId = null;
        });
    },
});

/**
 * True only when whatsappV2 holds a valid account + phone that belong to
 * `practiceId`. Use before building a WhatsApp URL from a practice id taken
 * from somewhere other than whatsappV2 (e.g. the route), which can move first.
 */
export const isWhatsappSelectionForPractice = (waState, practiceId) => (
    waState?.selectionPracticeId != null
    && practiceId != null
    && String(waState.selectionPracticeId) === String(practiceId)
    && hasValidWhatsappContext(waState?.currentBusinessId, waState?.currentWhatsappPhoneId)
);

// Reducer
export default slice.reducer;

export const {
    changeCurrentSearch,
    changeCurrentPhoneAndBusiness,
    saveWhatsappChatMetaData,
    resetWhatsappChat,
    updatePieSocketSubscriptions,
    updateSearchMode,
    updateSearchTag,
    clearAiAssistant,
    removeThreadFromList,
    removeThreadsFromList,
    applyThreadsRead,
    applyThreadsUnread,
    clearServiceWindowClosedDialog,
    applyMessageReaction,
    applyMessageRevoked,
    revertMessageRevoked,
    applyMessageStarred,
    applyMessageUnstarred,
    applyMessagePinned,
    applyMessageUnpinned,
    applyMessageEdited,
    revertMessageEdited,
    updateCurrentTemplateFavourite,
    receiveWhatsappAiStatus,
    clearWhatsappAiStatus,
    receiveWhatsappCallUpdate,
    receiveWhatsappCallRecordingUpdate,
    receiveWhatsappCallTranscriptSegment,
    setWhatsappActiveCall,
    clearWhatsappActiveCall,
} = slice.actions;

const getPracticeId = () => {
    const id = store.getState()?.practiceTreatmentList?.practiceId;
    if (!id || id === 'null' || id === 'undefined') return null;
    return id;
};

// The one place the thread-detail URL is built. Close, reopen, the lead-link
// PATCH and the refresh reads all address this resource, and the backend hides
// archived threads from it unless `is_archived_included=true` is on the query
// string. When close carried the flag and reopen did not, reopening a
// conversation whose thread had been archived 404ed on a row that existed
// (production thread 8601, retried by users). Every caller goes through here
// so the flag cannot be lost by one of them again.
export const threadDetailUrl = (practiceId, businessId, phoneId, threadId, extraParams = '') =>
    `/api/v1/practices/${practiceId}/whatsapp-business-accounts/${businessId}/whatsapp-phones/${phoneId}/whatsapp-message-threads/${threadId}/?is_archived_included=true${extraParams ? `&${extraParams}` : ''}`;

const getWhatsappThreadsFromStore = () => store.getState().whatsappV2?.threadMessages;

const buildWhatsappCallUrl = (suffix = '') => {
    const practiceId = getPracticeId();
    const state = store.getState()?.whatsappV2;
    const businessId = state?.currentBusinessId;
    const phoneId = state?.currentWhatsappPhoneId;
    if (!practiceId || !hasConnectedWhatsappWriteContext(state)) return null;
    return `/api/v1/practices/${practiceId}/whatsapp-business-accounts/${businessId}/whatsapp-phones/${phoneId}/${suffix}`;
};

const surfaceWhatsappCallError = (response, fallback) => (
    response?.data?.detail
    || response?.data?.error
    || response?.data?.message
    || fallback
);

export function fetchWhatsappCallingSettings() {
    return async () => {
        const url = buildWhatsappCallUrl('calling-settings/');
        if (!url) return null;
        try {
            dispatch(slice.actions.startCallingSettingsFetch());
            const response = await callApi(url, 'GET', null, false, { bustCache: true });
            if (response?.ok) {
                dispatch(slice.actions.completeCallingSettingsFetch(response?.data ?? null));
                return response;
            }
            const message = surfaceWhatsappCallError(response, 'Failed to load WhatsApp calling settings');
            dispatch(slice.actions.failCallingSettingsFetch(message));
            return response;
        } catch (error) {
            console.error('Error fetching WhatsApp calling settings', error);
            dispatch(slice.actions.failCallingSettingsFetch('Failed to load WhatsApp calling settings'));
            return null;
        }
    };
}

export function updateWhatsappCallingSettings(payload) {
    return async () => {
        const url = buildWhatsappCallUrl('calling-settings/');
        if (!url) return null;
        try {
            dispatch(slice.actions.startCallingSettingsFetch());
            const response = await callApi(url, 'PATCH', normalizeWhatsappCallingSettingsPayload(payload));
            if (response?.ok) {
                dispatch(slice.actions.completeCallingSettingsFetch(response?.data ?? null));
                dispatch(updateError({ success: true, message: 'WhatsApp calling settings updated' }));
                return response;
            }
            const message = surfaceWhatsappCallError(response, 'Failed to update WhatsApp calling settings');
            dispatch(slice.actions.failCallingSettingsFetch(message));
            dispatch(updateError({ success: false, message }));
            return response;
        } catch (error) {
            console.error('Error updating WhatsApp calling settings', error);
            dispatch(slice.actions.failCallingSettingsFetch('Failed to update WhatsApp calling settings'));
            dispatch(updateError({ success: false, message: 'Failed to update WhatsApp calling settings' }));
            return null;
        }
    };
}

export function fetchWhatsappCallDetail(callId) {
    return async () => {
        if (!callId) return null;
        const url = buildWhatsappCallUrl(`calls/${callId}/`);
        if (!url) return null;
        try {
            const response = await callApi(url, 'GET', null, false, { bustCache: true });
            if (response?.ok && response?.data?.id) {
                dispatch(slice.actions.receiveWhatsappCallUpdate(response.data));
            }
            return response;
        } catch (error) {
            console.error('Error fetching WhatsApp call detail', error);
            return null;
        }
    };
}

// F9 — fetch all call events for one thread so ChatPage can interleave them
// in the conversation timeline. Rows come from WhatsappCallSerializer and
// include direction / status / initiated_at / answered_at / ended_at and the
// server-computed `is_missed` (authoritative — never re-derive client-side).
// On any failure the thread's events are cleared (never left stale from a
// previously-open thread) and the error is logged.
export function fetchWhatsappThreadCalls(threadId) {
    return async () => {
        if (!threadId) return null;
        const url = buildWhatsappCallUrl(`calls/?thread=${threadId}&limit=100`);
        if (!url) return null;
        try {
            const response = await callApi(url, 'GET', null, false, { bustCache: true });
            if (response?.ok) {
                const calls = Array.isArray(response?.data?.results) ? response.data.results : [];
                dispatch(slice.actions.setThreadCallEvents({ calls, threadId }));
                return response;
            }
            console.error('Error fetching WhatsApp thread calls', response?.status, response?.error);
            dispatch(slice.actions.setThreadCallEvents({ calls: [], threadId }));
            return response;
        } catch (error) {
            console.error('Error fetching WhatsApp thread calls', error);
            dispatch(slice.actions.setThreadCallEvents({ calls: [], threadId }));
            return null;
        }
    };
}

export function connectWhatsappCall({ to, sdp, clientCallId, leadId, threadId }) {
    return async () => {
        const url = buildWhatsappCallUrl('calls/connect/');
        if (!url || !to || !sdp) return null;
        try {
            dispatch(slice.actions.startWhatsappCallAction());
            const payload = {
                to,
                sdp,
                client_call_id: clientCallId,
            };
            if (leadId) payload.lead_id = leadId;
            if (threadId) payload.thread_id = threadId;
            const response = await callApi(url, 'POST', payload);
            if (response?.ok && response?.data?.id) {
                dispatch(slice.actions.completeWhatsappCallAction(response.data));
            } else {
                dispatch(slice.actions.failWhatsappCallAction(surfaceWhatsappCallError(response, 'Failed to start WhatsApp call')));
            }
            return response;
        } catch (error) {
            console.error('Error starting WhatsApp call', error);
            dispatch(slice.actions.failWhatsappCallAction('Failed to start WhatsApp call'));
            return null;
        }
    };
}

export function preAcceptWhatsappCall(callId, sdp = null) {
    return async () => {
        if (!callId) return null;
        const url = buildWhatsappCallUrl(`calls/${callId}/pre_accept/`);
        if (!url) return null;
        try {
            dispatch(slice.actions.startWhatsappCallAction());
            const response = await callApi(url, 'POST', sdp ? { sdp } : {});
            if (response?.ok && response?.data?.id) {
                dispatch(slice.actions.completeWhatsappCallAction(response.data));
            } else {
                dispatch(slice.actions.failWhatsappCallAction(surfaceWhatsappCallError(response, 'Failed to prepare WhatsApp call')));
            }
            return response;
        } catch (error) {
            console.error('Error preparing WhatsApp call', error);
            dispatch(slice.actions.failWhatsappCallAction('Failed to prepare WhatsApp call'));
            return null;
        }
    };
}

export function acceptWhatsappCall(callId, sdp) {
    return async () => {
        if (!callId || !sdp) return null;
        const url = buildWhatsappCallUrl(`calls/${callId}/accept/`);
        if (!url) return null;
        try {
            dispatch(slice.actions.startWhatsappCallAction());
            const response = await callApi(url, 'POST', { sdp });
            if (response?.ok && response?.data?.id) {
                dispatch(slice.actions.completeWhatsappCallAction(response.data));
            } else {
                dispatch(slice.actions.failWhatsappCallAction(surfaceWhatsappCallError(response, 'Failed to answer WhatsApp call')));
            }
            return response;
        } catch (error) {
            console.error('Error answering WhatsApp call', error);
            dispatch(slice.actions.failWhatsappCallAction('Failed to answer WhatsApp call'));
            return null;
        }
    };
}

export function rejectWhatsappCall(callId) {
    return async () => {
        if (!callId) return null;
        const url = buildWhatsappCallUrl(`calls/${callId}/reject/`);
        if (!url) return null;
        try {
            dispatch(slice.actions.startWhatsappCallAction());
            const response = await callApi(url, 'POST', {});
            if (response?.ok && response?.data?.id) {
                dispatch(slice.actions.completeWhatsappCallAction(response.data));
            } else {
                dispatch(slice.actions.failWhatsappCallAction(surfaceWhatsappCallError(response, 'Failed to reject WhatsApp call')));
            }
            return response;
        } catch (error) {
            console.error('Error rejecting WhatsApp call', error);
            dispatch(slice.actions.failWhatsappCallAction('Failed to reject WhatsApp call'));
            return null;
        }
    };
}

export function terminateWhatsappCall(callId) {
    return async () => {
        if (!callId) return null;
        const url = buildWhatsappCallUrl(`calls/${callId}/terminate/`);
        if (!url) return null;
        try {
            dispatch(slice.actions.startWhatsappCallAction());
            const response = await callApi(url, 'POST', {});
            if (response?.ok && response?.data?.id) {
                dispatch(slice.actions.completeWhatsappCallAction(response.data));
            } else {
                dispatch(slice.actions.failWhatsappCallAction(surfaceWhatsappCallError(response, 'Failed to end WhatsApp call')));
            }
            return response;
        } catch (error) {
            console.error('Error terminating WhatsApp call', error);
            dispatch(slice.actions.failWhatsappCallAction('Failed to end WhatsApp call'));
            return null;
        }
    };
}

export function sendWhatsappCallPermissionRequest(to) {
    return async () => {
        const url = buildWhatsappCallUrl('call-permission-requests/');
        if (!url || !to) return null;
        try {
            dispatch(slice.actions.startWhatsappCallAction());
            const response = await callApi(url, 'POST', { to });
            dispatch(slice.actions.completeWhatsappCallAction());
            if (response?.ok) {
                dispatch(updateError({ success: true, message: 'Call permission request sent' }));
            } else {
                dispatch(updateError({
                    success: false,
                    message: surfaceWhatsappCallError(response, 'Failed to send call permission request'),
                }));
            }
            return response;
        } catch (error) {
            console.error('Error sending WhatsApp call permission request', error);
            dispatch(slice.actions.failWhatsappCallAction('Failed to send call permission request'));
            dispatch(updateError({ success: false, message: 'Failed to send call permission request' }));
            return null;
        }
    };
}

export function startWhatsappCallRecording(callId) {
    return async () => {
        if (!callId) return null;
        const url = buildWhatsappCallUrl(`calls/${callId}/recordings/start/`);
        if (!url) return null;
        try {
            const response = await callApi(url, 'POST', { provider: 'browser' });
            if (response?.ok && response?.data?.id) {
                dispatch(slice.actions.receiveWhatsappCallRecordingUpdate(response.data));
            }
            return response;
        } catch (error) {
            console.error('Error starting WhatsApp call recording', error);
            return null;
        }
    };
}

export function uploadWhatsappCallRecordingChunk(callId, recordingId, { blob, sequence, durationMs, mimeType }) {
    return async () => {
        if (!callId || !recordingId || !blob) return null;
        const url = buildWhatsappCallUrl(`calls/${callId}/recordings/${recordingId}/chunks/`);
        if (!url) return null;
        try {
            const formData = new FormData();
            formData.append('audio', blob, `whatsapp-call-${callId}-${sequence}.webm`);
            formData.append('sequence', String(sequence ?? 0));
            formData.append('duration_ms', String(durationMs ?? 0));
            formData.append('mime_type', mimeType || blob.type || 'audio/webm');
            const response = await callApi(url, 'POST', formData, false, { timeout: 30000 });
            if (response?.ok && response?.data?.recording?.id) {
                dispatch(slice.actions.receiveWhatsappCallRecordingUpdate(response.data.recording));
            }
            return response;
        } catch (error) {
            console.error('Error uploading WhatsApp call recording chunk', error);
            return null;
        }
    };
}

export function completeWhatsappCallRecording(callId, recordingId, { blob, durationMs, mimeType, transcriptText, errorMessage } = {}) {
    return async () => {
        if (!callId || !recordingId) return null;
        const url = buildWhatsappCallUrl(`calls/${callId}/recordings/${recordingId}/complete/`);
        if (!url) return null;
        try {
            const formData = new FormData();
            if (blob) formData.append('audio', blob, `whatsapp-call-${callId}.webm`);
            formData.append('duration_ms', String(durationMs ?? 0));
            formData.append('mime_type', mimeType || blob?.type || 'audio/webm');
            if (transcriptText) formData.append('transcript_text', transcriptText);
            if (errorMessage) formData.append('error_message', errorMessage);
            const response = await callApi(url, 'POST', formData, false, { timeout: 60000 });
            if (response?.ok && response?.data?.id) {
                dispatch(slice.actions.receiveWhatsappCallRecordingUpdate(response.data));
            }
            return response;
        } catch (error) {
            console.error('Error completing WhatsApp call recording', error);
            return null;
        }
    };
}

export function createWhatsappCallTranscriptionToken(callId, speaker) {
    return async () => {
        if (!callId) return null;
        const url = buildWhatsappCallUrl(`calls/${callId}/transcription-token/`);
        if (!url) return null;
        try {
            return await callApi(url, 'POST', {
                speaker,
                expires_in_seconds: 600,
                max_session_duration_seconds: 7200,
            });
        } catch (error) {
            console.error('Error creating WhatsApp call transcription token', error);
            return null;
        }
    };
}

export function saveWhatsappCallTranscriptSegment(callId, segment) {
    return async () => {
        if (!callId || !segment?.text) return null;
        const url = buildWhatsappCallUrl(`calls/${callId}/transcript/`);
        if (!url) return null;
        try {
            const response = await callApi(url, 'POST', segment);
            if (response?.ok && response?.data?.id) {
                dispatch(slice.actions.receiveWhatsappCallTranscriptSegment(response.data));
            }
            return response;
        } catch (error) {
            console.error('Error saving WhatsApp call transcript segment', error);
            return null;
        }
    };
}

export function fetchWhatsappCallTranscript(callId) {
    return async () => {
        if (!callId) return null;
        const url = buildWhatsappCallUrl(`calls/${callId}/transcript/`);
        if (!url) return null;
        try {
            const response = await callApi(url, 'GET', null, false, { bustCache: true });
            if (response?.ok && Array.isArray(response?.data)) {
                response.data.forEach((segment) => {
                    dispatch(slice.actions.receiveWhatsappCallTranscriptSegment(segment));
                });
            }
            return response;
        } catch (error) {
            console.error('Error fetching WhatsApp call transcript', error);
            return null;
        }
    };
}

// One run of the inbox count requests (5 thread counts + calls/counts).
// Returns `{ stalePractice: true }` when the practice changed while the
// requests were in flight, so the caller can re-run for the current practice
// instead of writing the old practice's numbers into the new one.
const runFilterCountsOnce = async (startLoadingState = true) => {
    if (startLoadingState)
        dispatch(slice.actions.startFetchWhatsappFilterCount());

    const filterCounts = {};

    try {
        const practiceId = getPracticeId();
        const state = store.getState()?.whatsappV2;
        const currentBusinessId = state?.currentBusinessId;
        const currentWhatsappPhoneId = state?.currentWhatsappPhoneId;

        if (!currentBusinessId || currentBusinessId === -1 || !currentWhatsappPhoneId || currentWhatsappPhoneId === -1) {
            dispatch(slice.actions.completeFetchWhatsappFilterCount({ filterCounts }));
            return { stalePractice: false };
        }

        // Stale-practice guard: IDs are valid but belong to a different practice.
        // Skip the cross-practice count fetch — complete with the current/empty
        // counts silently. Do NOT trigger re-init here; filter counts are passive,
        // and getWhatsaAppThreads will re-init when needed. Mirrors the guard shape
        // in getWhatsaAppThreads and fetchWhatsappChatAndOtherMetaData.
        const initializedForPracticeId = state?.initializedForPracticeId;
        if (initializedForPracticeId !== null && String(initializedForPracticeId) !== String(practiceId)) {
            dispatch(slice.actions.completeFetchWhatsappFilterCount({ filterCounts }));
            return { stalePractice: false };
        }

        const baseUrl = `/api/v1/practices/${practiceId}/whatsapp-business-accounts/${currentBusinessId}/whatsapp-phones/${currentWhatsappPhoneId}/whatsapp-message-threads/`;

        // F4: Widget counts are GLOBAL — never scoped to the current search
        // query. Previously `currentSearch` was used to prefix these URLs,
        // re-scoping widget counts to the search results and flashing zeros
        // while the user typed. Widgets must always reflect the full inbox
        // state so practitioners can trust the numbers at a glance.
        //
        // F1: FOLLOW_UP_COUNT ("Needs reply") and PENDING_COUNT ("Waiting on
        // patient") are direction-based (needs_reply param) so the widget, tab,
        // badge and list all agree. is_unread is reserved for the visual unread
        // dot; is_pending is deprecated on the BE.
        // F8: calls/counts/ lives one level up from the threads collection.
        const callCountsUrl = `/api/v1/practices/${practiceId}/whatsapp-business-accounts/${currentBusinessId}/whatsapp-phones/${currentWhatsappPhoneId}/calls/counts/`;

        // #19: ISO cutoff for the Waiting age breakdown — threads whose last
        // outbound is older than 7 days (B6 contract: last_outbound_at__lt is
        // a real FilterSet lookup; malformed datetime → 400). NULL caveat:
        // threads with no outbound ever have last_outbound_at=null and match
        // neither __lt nor __gte — correct here, because needs_reply=false
        // already requires at least one outbound.
        const waitingAgeCutoff = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString();

        // #22: the Activity row was trimmed to 3 cards (Needs reply · Missed
        // calls · Waiting), so the Window (service_window_closed) and New
        // leads (lead__isnull) count fetches were REMOVED — they had no other
        // consumers. Closed + Archived fetches REMAIN: they feed the sidebar
        // "More" menu badges in Contacts.jsx. The dashboard's NEW_LEAD_FILTER
        // is still populated independently by fetchUnreadWhatsAppCount.
        // S1: bustCache on every count call so rapid send sequences see updated
        // counts immediately rather than a stale dedupe-window response (32s window).
        const [needsReplyResponse, waitingResponse, archivedResponse, closedResponse, callCountsResponse, waitingOver7dResponse] = await Promise.all([
            callApi(`${baseUrl}?is_closed=false&needs_reply=true&is_archived_included=true&lead__is_archived=false&fields={}&limit=1`, 'GET', null, false, { bustCache: true }),
            callApi(`${baseUrl}?is_closed=false&needs_reply=false&is_archived_included=true&lead__is_archived=false&fields={}&limit=1`, 'GET', null, false, { bustCache: true }),
            callApi(`${baseUrl}?lead__is_archived=true&fields={}&limit=1`, 'GET', null, false, { bustCache: true }),
            callApi(`${baseUrl}?is_closed=true&fields={}&limit=1`, 'GET', null, false, { bustCache: true }),
            // F8: missed-call count for the "Missed calls" activity widget.
            // callApi resolves { ok:false } (never throws) so a failure here
            // cannot sink the whole Promise.all.
            callApi(callCountsUrl, 'GET', null, false, { bustCache: true }),
            // #19: Waiting >7d — the ONE extra count request allowed by the
            // contract. Same base filters as PENDING_COUNT plus the age cutoff.
            callApi(`${baseUrl}?is_closed=false&needs_reply=false&is_archived_included=true&lead__is_archived=false&last_outbound_at__lt=${encodeURIComponent(waitingAgeCutoff)}&fields={}&limit=1`, 'GET', null, false, { bustCache: true }),
        ]);
        // Practice switched while the counts were in flight: these numbers
        // belong to the old practice. Drop them; the caller re-runs.
        if (String(getPracticeId()) !== String(practiceId)) {
            return { stalePractice: true };
        }

        // callApi never throws; a failed count (ok=false) keeps the number we
        // already show instead of flashing it to 0.
        const previous = store.getState()?.whatsappV2?.filterCounts ?? {};
        const countFrom = (response, key, field = 'count') => (
            response?.ok ? (response?.data?.[field] ?? 0) : (previous[key] ?? 0)
        );
        filterCounts.FOLLOW_UP_COUNT = countFrom(needsReplyResponse, 'FOLLOW_UP_COUNT');
        filterCounts.PENDING_COUNT = countFrom(waitingResponse, 'PENDING_COUNT');
        filterCounts.ARCHIVED_COUNT = countFrom(archivedResponse, 'ARCHIVED_COUNT');
        filterCounts.CLOSED_COUNT = countFrom(closedResponse, 'CLOSED_COUNT');
        // F8: `missed` is the server-computed unanswered-inbound count from
        // calls/counts/. Render 0 as a real zero — '—' only when never loaded.
        filterCounts.MISSED_CALLS_COUNT = countFrom(callCountsResponse, 'MISSED_CALLS_COUNT', 'missed');
        // #19: Waiting-on-patient threads whose last outbound is >7 days old.
        filterCounts.WAITING_OVER_7D_COUNT = countFrom(waitingOver7dResponse, 'WAITING_OVER_7D_COUNT');

    } catch (err) {
        console.error("Error while fetching whatsapp filter counts", err);
    }

    dispatch(slice.actions.completeFetchWhatsappFilterCount({ filterCounts }));
    return { stalePractice: false };
};

// ── Count coalescing (CU-869fn2znc) ──────────────────────────────────────
// Every `new-whatsapp-message` socket event, in every open tab and on every
// page, used to re-run all six count requests. A burst of 10 messages in 2 s
// sent 60 count requests per tab. Two rules now bound that:
//
// 1. Single-flight: while a count run is in flight, further calls start no
//    new requests. They mark the run dirty, and exactly one follow-up run
//    starts when the current one settles, so the final numbers always
//    reflect the latest state.
// 2. Background (socket) refreshes are throttled to one run per
//    FILTER_COUNTS_BACKGROUND_WINDOW_MS, leading + trailing: the first one
//    runs at once, the rest collapse into one trailing run at the end of the
//    window. User actions (open inbox, change filter, send, bulk actions)
//    stay immediate and only go through single-flight.
export const FILTER_COUNTS_BACKGROUND_WINDOW_MS = 10000;
let filterCountsInFlight = null;
let filterCountsDirty = false;
let filterCountsLastRunAt = 0;
let filterCountsTrailingTimer = null;

const clearFilterCountsTrailingTimer = () => {
    if (filterCountsTrailingTimer) {
        clearTimeout(filterCountsTrailingTimer);
        filterCountsTrailingTimer = null;
    }
};

const fetchFilterCounts = (startLoadingState = true) => {
    if (filterCountsInFlight) {
        filterCountsDirty = true;
        return filterCountsInFlight;
    }
    const run = async () => {
        let loading = startLoadingState;
        try {
            do {
                filterCountsDirty = false;
                filterCountsLastRunAt = Date.now();
                // This run starts after any queued socket event, so it
                // already covers a pending trailing run.
                clearFilterCountsTrailingTimer();
                // eslint-disable-next-line no-await-in-loop
                const result = await runFilterCountsOnce(loading);
                loading = false;
                if (result?.stalePractice) filterCountsDirty = true;
            } while (filterCountsDirty);
        } finally {
            // Cleared in the same tick as the last dirty check, so a caller
            // can never mark a run dirty after the loop has decided to stop.
            filterCountsInFlight = null;
        }
    };
    filterCountsInFlight = run();
    return filterCountsInFlight;
};

// Throttled entry point for socket-driven (background) refreshes.
const fetchFilterCountsInBackground = () => {
    if (filterCountsTrailingTimer) return; // a trailing run is already queued
    const wait = filterCountsLastRunAt + FILTER_COUNTS_BACKGROUND_WINDOW_MS - Date.now();
    if (wait <= 0) {
        fetchFilterCounts(false);
        return;
    }
    // If the practice changes before the timer fires, drop the run: the new
    // practice's inbox load fetches its own counts.
    const scheduledForPracticeId = getPracticeId();
    filterCountsTrailingTimer = setTimeout(() => {
        filterCountsTrailingTimer = null;
        if (String(getPracticeId()) !== String(scheduledForPracticeId)) return;
        fetchFilterCounts(false);
    }, wait);
};

// Test-only: module state survives between tests in one file.
export const resetFilterCountsCoalescingForTests = () => {
    clearFilterCountsTrailingTimer();
    filterCountsInFlight = null;
    filterCountsDirty = false;
    filterCountsLastRunAt = 0;
};

export function handleAddWhatsappNotificationCount(reset = false) {
    return async () => {
        const whatsappNotifCount = store.getState()?.whatsappV2?.whatsappNotifCount ?? 0;
        if (reset)
            dispatch(slice.actions.addWhatsappNotifCount(0));
        else
            dispatch(slice.actions.addWhatsappNotifCount(whatsappNotifCount + 1));
    }
}

// Fetch WhatsApp counts for widget/dashboard (unread, new leads, pending)
export function fetchUnreadWhatsAppCount() {
    return async () => {
        if (!hasFeatureAccess('whatsapp-messaging')) return { needsReply: 0, newLeads: 0, waiting: 0 };
        try {
            const practiceId = getPracticeId();
            if (!practiceId) return { needsReply: 0, newLeads: 0, waiting: 0 };

            // First get WhatsApp business accounts
            const accountsResponse = await callApi(
                `/api/v1/practices/${practiceId}/whatsapp-business-accounts/`
            );
            const accounts = accountsResponse?.data?.results || [];
            if (accounts.length === 0) return { needsReply: 0, newLeads: 0, waiting: 0 };

            const businessId = accounts[0]?.id;
            if (!businessId) return { needsReply: 0, newLeads: 0, waiting: 0 };

            // Get phones for this business account
            const phonesResponse = await callApi(
                `/api/v1/practices/${practiceId}/whatsapp-business-accounts/${businessId}/whatsapp-phones/`
            );
            const phones = phonesResponse?.data?.results || [];
            if (phones.length === 0) return { needsReply: 0, newLeads: 0, waiting: 0 };

            const phoneId = phones[0]?.id;
            if (!phoneId) return { needsReply: 0, newLeads: 0, waiting: 0 };

            const baseUrl = `/api/v1/practices/${practiceId}/whatsapp-business-accounts/${businessId}/whatsapp-phones/${phoneId}/whatsapp-message-threads/`;

            // F1: direction-based counts. needsReply = patient sent last message
            // OR unanswered missed call (needs_reply=true); waiting = practice
            // sent last + at least one outbound exists (needs_reply=false).
            // is_pending is deprecated on the BE; is_unread is only for the
            // visual unread dot and must not drive any count here.
            // S1 (fetchUnreadWhatsAppCount): bustCache so rapid interactions
            // start a fresh request instead of joining an in-flight dedupe chain.
            const [needsReplyResponse, newLeadsResponse, waitingResponse] = await Promise.all([
                callApi(`${baseUrl}?needs_reply=true&is_closed=false&is_archived_included=true&lead__is_archived=false&fields={}&limit=1`, 'GET', null, false, { bustCache: true }),
                callApi(`${baseUrl}?lead__isnull=true&is_closed=false&is_archived_included=true&fields={}&limit=1`, 'GET', null, false, { bustCache: true }),
                callApi(`${baseUrl}?needs_reply=false&is_closed=false&is_archived_included=true&lead__is_archived=false&fields={}&limit=1`, 'GET', null, false, { bustCache: true }),
            ]);

            const needsReplyCount = needsReplyResponse?.data?.count || 0;
            const newLeadsCount = newLeadsResponse?.data?.count || 0;
            const waitingCount = waitingResponse?.data?.count || 0;

            // Update the filter counts in store.
            // #27 nit: the UNREAD_FILTER and PENDING_FILTER writes were dead —
            // no component reads either key (repo-wide grep 2026-07-02); the
            // widgets/tabs consume FOLLOW_UP_COUNT / PENDING_COUNT and the
            // dashboard consumes NEW_LEAD_FILTER. Removed rather than kept
            // in sync with semantics they no longer describe.
            const currentFilterCounts = store.getState()?.whatsappV2?.filterCounts || {};
            dispatch(slice.actions.completeFetchWhatsappFilterCount({
                filterCounts: {
                    ...currentFilterCounts,
                    [WHATSAPP_FILTERS.NEW_LEAD_FILTER]: newLeadsCount,
                    FOLLOW_UP_COUNT: needsReplyCount,
                    PENDING_COUNT: waitingCount,
                }
            }));

            // Store in widgetCounter so dashboard reads stable counts
            // independent of WhatsApp page filter state.
            // F1: the dashboard card count = needs-reply count ONLY, so the
            // widget number equals the "Needs reply" tab's list length
            // (widget == tab == badge == list). newLeads/waiting stay available
            // for secondary badge text.
            dispatch(upsertWidgetCounter({
                key: '7',
                count: needsReplyCount,
                whatsapp: { needsReply: needsReplyCount, newLeads: newLeadsCount, waiting: waitingCount }
            }));

            return { needsReply: needsReplyCount, newLeads: newLeadsCount, waiting: waitingCount };
        } catch (error) {
            // Silent fail - widget will show 0
            return { needsReply: 0, newLeads: 0, waiting: 0 };
        }
    }
}

export function syncTemplates() {
    return async () => {
        try {
            dispatch(updateError({ message: "Template Sync In Progress!", success: "warning" }));
            const practiceId = getPracticeId();
            const state = store.getState()?.whatsappV2;
            const currentBusinessId = state?.currentBusinessId;
            if (!hasConnectedWhatsappWriteContext(state)) {
                dispatch(updateError({ message: "WhatsApp is still loading, please try again", success: "warning" }));
                return;
            }
            const generation = beginTemplateRefresh(practiceId, currentBusinessId);
            await callApi(
                `/api/v1/practices/${practiceId}/whatsapp-business-accounts/${currentBusinessId}/sync_templates/`,
                'POST'
            );
            await fetchAndPublishWhatsappTemplates({
                practiceId,
                businessId: currentBusinessId,
                generation,
            });
            dispatch(updateError({ message: "Successfully synced the templates!", success: true }));
        } catch (error) {
            console.error("Failed to sync templates");
            dispatch(updateError({ message: "Failed to sync templates!", success: false }));
        }
    }
}

const fetchAndPublishWhatsappTemplates = async ({ practiceId, businessId, generation }) => {
    const templateResponse = await callApi(
        `/api/v1/practices/${practiceId}/whatsapp-business-accounts/${businessId}/whatsapp-message-templates/`
    );
    if (!(templateResponse?.status === 200 || templateResponse?.ok)) return null;
    if (!isLatestTemplateRefresh(practiceId, businessId, generation)) return null;
    if (String(getPracticeId()) !== String(practiceId)) return null;
    const templates = templateResponse?.data?.results ?? [];
    dispatch(slice.actions.saveSyncedTemplates({ practiceId, businessId, templates }));
    return templates;
};

export function refreshWhatsappTemplatesForBusiness({ practiceId, businessId }) {
    return async () => fetchAndPublishWhatsappTemplates({
        practiceId,
        businessId,
        generation: beginTemplateRefresh(practiceId, businessId),
    });
}
const unsubscribeConnections = async (subs) => {
    try {
        subs.forEach(sub => {
            if (sub.connection) {
                sub.connection.unsubscribe(sub?.roomId);
            }
        });
    } catch (error) {
        console.error("Error in unsubscribing connection")
    }
}

export function initializeWhatsapp(retryAttempt = 0) {
    return async () => {
        if (!hasFeatureAccess('whatsapp-messaging')) return;
        // Capture the generation at the very start of this invocation (after the
        // feature-flag guard so a disabled feature never bumps the counter).
        // Any subsequent initializeWhatsapp() call will increment waInitGeneration;
        // this call then checks waInitGeneration !== myGen before touching Redux
        // state and silently returns if superseded — preventing a slower
        // practice-switch init from stamping stale IDs over a faster one.
        waInitGeneration += 1;
        const myGen = waInitGeneration;
        // Hoisted so the catch block can include practiceId in the noConfig
        // completeWhatsappInitialization dispatch (needed for the stale-IDs guard).
        const practiceId = getPracticeId();
        let historicalBusinessAccounts = [];
        try {
            const updatePieSocketSubscriptions = store.getState()?.whatsappV2?.pieSocketSubscriptions;
            dispatch(slice.actions.startInitializeWhatsapp());
            await unsubscribeConnections(updatePieSocketSubscriptions);
            // Get all Available Whatsapp Business Accounts
            const availableWhatsaAppBusinessAccResp = await callApi(
                `/api/v1/practices/${practiceId}/whatsapp-business-accounts/`,
                'GET'
            );

            if (!availableWhatsaAppBusinessAccResp?.ok) {
                // Request failed (5xx/network/timeout) — TRANSIENT, not "no config". Let the catch retry.
                throw new Error("WhatsApp accounts request failed");
            }
            const businessAccounts = normalizeWhatsappBusinessAccounts(
                availableWhatsaAppBusinessAccResp?.data
            );
            const connectedBusinessAccounts = getConnectedWhatsappBusinessAccounts(businessAccounts);
            historicalBusinessAccounts = getHistoricalWhatsappBusinessAccounts(businessAccounts);
            const businessIdList = businessAccounts.map((account) => account?.id).filter(Boolean);
            if (!businessIdList?.length) {
                throw new Error("No Whatsapp Business Account Found!");
            }

            const businessAccountConnectionMap = Object.fromEntries(
                businessAccounts.map((account) => [
                    String(account?.id),
                    isConnectedWhatsappBusinessAccount(account),
                ])
            );

            const businessIdToPhone = {};

            const fetchAllBusinessData = async () => {
                const results = await Promise.all(
                    businessIdList.map(async (businessId) => {
                        const availablePhoneNumbersWithBID = await callApi(
                            `/api/v1/practices/${practiceId}/whatsapp-business-accounts/${businessId}/whatsapp-phones/`,
                            'GET'
                        );
                        if (!availablePhoneNumbersWithBID?.ok) {
                            // Request failed (5xx/network/timeout) — TRANSIENT, not "no config".
                            // Reject the Promise.all so the outer catch retries.
                            throw new Error("WhatsApp phones request failed");
                        }
                        return { businessId, data: availablePhoneNumbersWithBID?.data?.results ?? [] };
                    })
                );

                results?.forEach(({ businessId, data }) => {
                    businessIdToPhone[businessId] = data;
                });

                return businessIdToPhone;
            };

            await fetchAllBusinessData();

            const defaultConnectedBusinessId = connectedBusinessAccounts
                .map((account) => String(account?.id))
                .find((businessId) => businessIdToPhone?.[businessId]?.length > 0);
            const defaultBusinessId = defaultConnectedBusinessId
                ?? Object.keys(businessIdToPhone ?? {}).find(
                    (businessId) => businessIdToPhone?.[businessId]?.length > 0
                )
                ?? -1;
            const defaultPhoneId = businessIdToPhone?.[defaultBusinessId]?.[0]?.id ?? -1;

            if (defaultBusinessId === -1 || defaultPhoneId === -1) {
                // dispatch(updateError({ message: "No Whatsapp Phone Number Found!", success: false }));
                throw new Error("No Whatsapp Phone Number Found!")
            }

            const jwtTokens = {};
            const templates = {};
            let defaultTemplates = [];

            const fetchJWTTokensAndTemplates = async () => {
                await Promise.all([
                    Promise.all(connectedBusinessAccounts.map(async (account) => {
                        const businessId = String(account?.id);
                        const phoneIdObj = {};

                        await Promise.all(
                            (businessIdToPhone?.[businessId] ?? []).map(async (phoneObj) => {
                                const jwtResp = await callApi(
                                    `/api/v1/practices/${practiceId}/whatsapp-business-accounts/${businessId}/whatsapp-phones/${phoneObj?.id}/generate_websocket_jwt/`,
                                    'POST'
                                );
                                phoneIdObj[phoneObj.id] = jwtResp?.data?.auth;
                            })
                        );

                        jwtTokens[businessId] = phoneIdObj;
                    })),
                    Promise.all(businessIdList.map(async (accountId) => {
                        const businessId = String(accountId);
                        const templateResponse = await callApi(
                            `/api/v1/practices/${practiceId}/whatsapp-business-accounts/${businessId}/whatsapp-message-templates/`
                        );

                        templates[businessId] = templateResponse?.data?.results ?? [];
                        if (businessId === defaultBusinessId) {
                            defaultTemplates = templateResponse?.data?.results ?? [];
                        }
                    })),
                ]);
            };

            await fetchJWTTokensAndTemplates();

            // Choices are owned by the header's getChoicesList() (fired on every
            // practiceId change). Re-use the store copy instead of re-fetching —
            // this was a confirmed duplicate request on every dashboard load.
            // Guard: choicesData carries no practice id by default; _practiceId is
            // stamped by getChoicesList() so we can detect stale-practice data
            // (practice A choices still cached when init runs for practice B).
            const choicesData = store.getState()?.practiceTreatment?.choicesData;
            const storeChoicesMatch = String(choicesData?._practiceId) === String(practiceId);
            let choiceResults = storeChoicesMatch ? choicesData?.results : null;
            if (!choiceResults?.length) {
                // Not loaded yet (e.g. direct WhatsApp-page deep link raced the
                // header), or stale from a previous practice — fetch once; the 8s
                // callApi dedupe collapses a concurrent header fetch onto this promise.
                const practiceChoices = await callApi(`/api/v1/practices/${practiceId}/choices/`);
                choiceResults = practiceChoices?.data?.results;
            }

            const choices = choiceResults?.filter(choice => choice?.metadata?.action === "close_thread" && choice?.metadata?.resource === "whatsapp_message_thread")?.[0]?.items?.map(choice => choice?.value ?? "");

            // Extract AI Config from practice choices
            const aiConfigChoice = choiceResults?.find(choice => /ai\s*config/i.test(String(choice?.name || '')));
            const aiConfig = aiConfigChoice?.metadata || null;

            // Extract WhatsApp template usage type filter from practice choices
            const waTemplateConfig = choiceResults?.find(
                choice => choice?.metadata?.action === 'template_filter' && choice?.metadata?.resource === 'whatsapp_template'
            );
            const allowedUsageTypes = waTemplateConfig?.items?.length > 0
                ? waTemplateConfig.items.map(item => item?.value).filter(Boolean)
                : null;

            // Extract Email template usage type filter from practice choices
            const emailTemplateConfig = choiceResults?.find(
                choice => choice?.metadata?.action === 'template_filter' && choice?.metadata?.resource === 'email_template'
            );
            const allowedEmailUsageTypes = emailTemplateConfig?.items?.length > 0
                ? emailTemplateConfig.items.map(item => item?.value).filter(Boolean)
                : null;

            // Bail out if a newer initializeWhatsapp() was dispatched while we
            // were awaiting API responses — prevents a slower init (e.g. for
            // the old practice) from stamping stale IDs over a completed one.
            if (waInitGeneration !== myGen) return;
            // Practice-scope guard (#41): the generation counter only orders
            // concurrent inits by dispatch order — it does NOT verify this
            // init's captured practiceId is still the live one. If the user
            // switched practices while we awaited, stamping here would write
            // this practice's IDs (or, on the noConfig path below, a -1 that
            // flips whatsappNotConfigured) onto the practice now in view. Bail;
            // the live practice's own init (captured id == live) does the stamp.
            if (String(getPracticeId()) !== String(practiceId)) {
                // Release the loading flag this init raised. Bailing without it
                // wedges the slice permanently — see cancelInitializeWhatsapp.
                dispatch(slice.actions.cancelInitializeWhatsapp());
                return;
            }

            dispatch(slice.actions.completeWhatsappInitialization({
                businessIdToPhoneMap: businessIdToPhone,
                businessAccountConnectionMap,
                currentBusinessId: defaultBusinessId,
                currentPhoneid: defaultPhoneId,
                currentBusinessIsConnected: businessAccountConnectionMap?.[defaultBusinessId] === true,
                historicalBusinessAccounts,
                jwtTokens,
                templates,
                currentWhatsappTemplate: defaultTemplates,
                practiceChoices: choices,
                aiConfig,
                allowedUsageTypes,
                allowedEmailUsageTypes,
                practiceId,
            }))

        } catch (error) {
            const noConfig =
                error?.message === "No Whatsapp Phone Number Found!" ||
                error?.message === "No Whatsapp Business Account Found!" ||
                error?.message === "No Connected Whatsapp Business Account Found!";
            // Genuinely no WhatsApp set up (definitive — only thrown on an OK-but-empty response) → clean empty state, no retry.
            if (noConfig) {
                // Superseded init must not clobber a newer practice's noConfig stamp.
                if (waInitGeneration !== myGen) return;
                // Practice-scope guard (#41): don't stamp whatsappNotConfigured=-1
                // for a practice the user has already left — that's exactly how a
                // connected practice ended up showing the "connect WhatsApp" empty
                // state. See the matching guard on the success path above.
                if (String(getPracticeId()) !== String(practiceId)) {
                    // Same wedge as the success path above.
                    dispatch(slice.actions.cancelInitializeWhatsapp());
                    return;
                }
                dispatch(slice.actions.completeWhatsappInitialization({
                    currentBusinessId: -1,
                    currentBusinessIsConnected: false,
                    historicalBusinessAccounts,
                    practiceId,
                }));
                return;
            }
            console.error("Error in initializeWhatsapp", error);
            // Transient bootstrap failure (incl. the new "request failed" throws) → retry with backoff; don't clobber IDs.
            if (retryAttempt < 2) {
                await wait(1500 * (retryAttempt + 1));
                // If superseded during the wait, skip the retry — a newer init is
                // already handling the correct practice.
                if (waInitGeneration !== myGen) return;
                dispatch(initializeWhatsapp(retryAttempt + 1));
                return;
            }
            // Persistent real failure → distinct terminal state (NOT "not configured").
            // Only stamp failure if we're still the active init.
            if (waInitGeneration !== myGen) return;
            dispatch(slice.actions.failWhatsappInitialization());
        }
    }
}

// Fetch AI wizard status for a practice
export function fetchAIWizardStatus(practiceId) {
    return async () => {
        if (!practiceId) return;
        try {
            const response = await callApi(`/api/v1/practices/${practiceId}/ai-config/wizard-status/`);
            if (response.ok) {
                dispatch(slice.actions.setAiWizardStatus(response.data));
            }
        } catch (error) {
            console.error("Error fetching AI wizard status", error);
        }
    };
}

// Fetch AI wizard config for a practice (from /ai-config/ endpoint)
export function fetchAIConfig(practiceId) {
    return async () => {
        if (!practiceId) return;
        try {
            dispatch(slice.actions.setAiWizardConfigLoading(true));
            const response = await callApi(`/api/v1/practices/${practiceId}/ai-config/`);
            if (response.ok) {
                dispatch(slice.actions.setAiWizardConfig(response.data));
            }
        } catch (error) {
            console.error("Error fetching AI config", error);
        } finally {
            dispatch(slice.actions.setAiWizardConfigLoading(false));
        }
    };
}

// Update AI wizard config for a practice
export function updateAIConfig(practiceId, data) {
    return async () => {
        dispatch(slice.actions.setAiWizardConfigLoading(true));
        const response = await callApi(`/api/v1/practices/${practiceId}/ai-config/`, 'PATCH', data);
        dispatch(slice.actions.setAiWizardConfigLoading(false));
        if (!response.ok) {
            return { ok: false, error: response.data?.error || response.data?.detail || 'Failed to save' };
        }
        dispatch(slice.actions.setAiWizardConfig(response.data));
        return { ok: true };
    };
}

// Legacy: Fetch AI config from practice choices (can be called independently without full WhatsApp init)
export function fetchAiConfig(practiceId) {
    return async () => {
        if (!practiceId) return;

        try {
            dispatch(slice.actions.setAiConfigLoading(true));
            const practiceChoices = await callApi(`/api/v1/practices/${practiceId}/choices/`);

            // Extract AI Config from practice choices
            const aiConfigChoice = practiceChoices?.data?.results?.find(choice => /ai\s*config/i.test(String(choice?.name || '')));
            const aiConfig = aiConfigChoice?.metadata || null;

            dispatch(slice.actions.setAiConfig(aiConfig));
        } catch (error) {
            console.error("Error fetching AI config", error);
        } finally {
            dispatch(slice.actions.setAiConfigLoading(false));
        }
    }
}

// Fetch template filter configs from practice choices (can be called independently without full WhatsApp init)
export function fetchTemplateFilterConfigs(practiceId) {
    return async () => {
        if (!practiceId) return;

        try {
            const practiceChoices = await callApi(`/api/v1/practices/${practiceId}/choices/?limit=200`);

            const waTemplateConfig = practiceChoices?.data?.results?.find(
                choice => choice?.metadata?.action === 'template_filter' && choice?.metadata?.resource === 'whatsapp_template'
            );
            const allowedUsageTypes = waTemplateConfig?.items?.length > 0
                ? waTemplateConfig.items.map(item => item?.value).filter(Boolean)
                : null;

            const emailTemplateConfig = practiceChoices?.data?.results?.find(
                choice => choice?.metadata?.action === 'template_filter' && choice?.metadata?.resource === 'email_template'
            );
            const allowedEmailUsageTypes = emailTemplateConfig?.items?.length > 0
                ? emailTemplateConfig.items.map(item => item?.value).filter(Boolean)
                : null;

            dispatch(slice.actions.setTemplateFilterConfigs({ allowedUsageTypes, allowedEmailUsageTypes }));
        } catch (error) {
            console.error("Error fetching template filter configs", error);
        }
    }
}

export function updateSubChannel(subs) {
    return async () => {
        const pieSocketSubscriptions = store.getState()?.whatsappV2?.pieSocketSubscriptions;
        dispatch(slice.actions.updatePieSocketSubscriptions([...pieSocketSubscriptions, subs]));
    }
}

const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

// eslint-disable-next-line no-unused-vars
const getCampaigns = async () => {
    try {
        const practiceId = getPracticeId();
        const api = `/api/v1/practices/${practiceId}/campaigns/?&channel=WHATSAPP&ordering=-created_at&status__in=SENT`;
        const response = await callApi(api, 'GET');
        const idMap = response?.data?.results?.map(obj => obj?.id)?.join(",");
        if (idMap?.length) return idMap;
        return -1;
    } catch (error) {
        console.error("error in campaigns");
        return "-1";
    }
}

// Monotonic sequence for sidebar thread-list fetches.
//
// 2026-08-28 BUG FIX — clearing the search box left the previous search
// results on screen. Two thread fetches can be in flight at once (the slow
// `search/` endpoint, then the plain list the operator triggers by clearing
// the field) and NOTHING serialised them: the `isLoading` branch below guards
// the *initialisation* flag, while thread fetches raise `threadLoading`. So
// whichever HTTP response arrived LAST won `completeFetchWhatsappThreads` —
// and `search/` (7 TrigramSimilarity annotations, one of them across
// whatsapp_messages__body) is systematically slower than the plain list, so
// the stale search response reliably landed after the cleared-search list and
// overwrote it.
//
// Every fetch takes a ticket; only the newest ticket may commit. Shared with
// the fetchNext branch on purpose — appending page 2 of a superseded list onto
// a freshly-loaded one is the same bug wearing a different hat.
let threadFetchToken = 0;

export function getWhatsaAppThreads(fetchNext = false, retryAttempt = 0) {
    return async () => {
        if (!hasFeatureAccess('whatsapp-messaging')) return;
        // Ticket for this call, claimed only by the two branches that actually
        // hit the network. Stays null on the guard/retry paths so they never
        // invalidate an in-flight fetch. Declared outside the try so the catch
        // can tell "my request failed" from "I was superseded".
        let fetchToken = null;
        try {
            const practiceId = getPracticeId();
            const currentBusinessId = store.getState()?.whatsappV2?.currentBusinessId;
            const currentWhatsappPhoneId = store.getState()?.whatsappV2?.currentWhatsappPhoneId;
            const currentSearch = store.getState()?.whatsappV2?.currentSearch;
            const threadNextAPI = store.getState()?.whatsappV2?.threadNextAPI;
            const currentThreadMessage = store.getState()?.whatsappV2?.threadMessages;
            const searchMode = store.getState()?.whatsappV2?.searchMode;
            const searchTag = store.getState()?.whatsappV2?.currentTag;
            const isLoading = store.getState()?.whatsappV2?.isLoading;
            const currentUser = store.getState()?.whatsappV2?.currentUser;
            let threadMessages = [];
            let threadNext = "";

            const isBusinessInvalid = !currentBusinessId || currentBusinessId === -1;
            const isPhoneInvalid = !currentWhatsappPhoneId || currentWhatsappPhoneId === -1;
            const whatsappNotConfigured = store.getState()?.whatsappV2?.whatsappNotConfigured;
            const whatsappInitError = store.getState()?.whatsappV2?.whatsappInitError;

            // Stale-practice guard: the WA IDs in state belong to a different
            // practice than the current practiceId. Firing the thread URL with
            // mismatched IDs produces a 403 and an empty "No conversations" state.
            // When the IDs are genuinely valid but stale, trigger re-init instead.
            // When already loading, the existing isLoading retry branches below
            // handle the wait naturally (no separate stale+loading branch needed).
            const initializedForPracticeId = store.getState()?.whatsappV2?.initializedForPracticeId;
            // null = never successfully initialized → let the invalid-ID branches
            // above handle it; don't conflate "not yet initialized" with "stale".
            const staleIds = !isBusinessInvalid && !isPhoneInvalid &&
                initializedForPracticeId !== null &&
                String(initializedForPracticeId) !== String(practiceId);
            if (staleIds && !isLoading) {
                // Synchronous loading-flip BEFORE the async re-init: prevents a
                // concurrent getWhatsaAppThreads call from re-entering this recovery
                // branch and firing a second initializeWhatsapp (double toast / double
                // init race). startInitializeWhatsapp now preserves pieSocketSubscriptions
                // so initializeWhatsapp() can still read + unsubscribe old-practice
                // sockets correctly — see reducer comment for details.
                dispatch(slice.actions.startInitializeWhatsapp());
                dispatch(initializeWhatsapp());
                return;
            }

            if ((isBusinessInvalid || isPhoneInvalid) && !isLoading && whatsappNotConfigured) {
                // Practice has no WhatsApp Business account configured — show the empty state, no error
            } else if ((isBusinessInvalid || isPhoneInvalid) && !isLoading && whatsappInitError) {
                dispatch(updateError({ message: "Couldn't load WhatsApp — retrying…", success: "warning" }));
                // Synchronously flip into loading state (sets isLoading=true and resets
                // whatsappInitError=false) BEFORE the async re-init, so a concurrent
                // getWhatsaAppThreads sees isLoading and takes the loading branch instead
                // of re-entering recovery (double toast + concurrent init).
                dispatch(slice.actions.startInitializeWhatsapp());
                // Fire-and-forget: Contacts.jsx useEffect re-dispatches getWhatsaAppThreads once IDs become valid.
                dispatch(initializeWhatsapp());
            } else if (((isBusinessInvalid || isPhoneInvalid) && !isLoading) && retryAttempt <= 3) {
                dispatch(updateError({ message: "Loading whatsapp messages", success: "warning" }));
                await wait(2000 * retryAttempt);
                dispatch(getWhatsaAppThreads(fetchNext, retryAttempt + 1));
            } else if ((isBusinessInvalid || isPhoneInvalid) && !isLoading) {
                dispatch(updateError({ message: "WhatsApp is taking longer than usual — please refresh.", success: "warning" }));
            } else if (isLoading && retryAttempt <= 3) {
                await wait(2000 * retryAttempt);
                dispatch(getWhatsaAppThreads(fetchNext, retryAttempt + 1));
            } else if (isLoading) {
                // Retries exhausted and still loading: the slice is wedged (see
                // cancelInitializeWhatsapp). Clear the flag and mark the init
                // failed BEFORE throwing, so the whatsappInitError branch above
                // can re-initialise on the next call. Without this the operator
                // is stuck behind a toast until they reload the page.
                dispatch(slice.actions.failWhatsappInitialization());
                throw new Error("Stuck in Loading");
            } else if (fetchNext) {
                // Guard: empty cursor means there is no next page (e.g. after a
                // practice switch that reset threadNextAPI to ""). Without this,
                // `"".match(/api\/v1.*/)` → null → callApi('/null') → 404 error →
                // catch dispatches completeThreadNextFetch({threadMessages:[]}) →
                // `[]` passes the ?? check and silently wipes the visible list.
                if (!threadNextAPI) return;

                // No count refresh here: paging loads more rows of the same
                // list, and the counts don't change when you scroll.
                dispatch(slice.actions.startThreadNextFetch());
                threadFetchToken += 1;
                fetchToken = threadFetchToken;
                const result = threadNextAPI.match(/api\/v1.*/);
                const extractedPath = result ? result[0] : null;
                const threadResponse = await callApi(`/${extractedPath}`, 'GET');
                // Superseded while in flight (search cleared, filter changed,
                // practice switched) — drop the page rather than appending it
                // to a list it no longer belongs to.
                if (fetchToken !== threadFetchToken) return;
                threadMessages = [...currentThreadMessage, ...(threadResponse?.data?.results ?? [])];
                threadNext = threadResponse?.data?.next ?? "";
                dispatch(slice.actions.completeThreadNextFetch({
                    threadMessages,
                    threadNext
                }))
            } else {
                fetchFilterCounts();
                dispatch(slice.actions.startFetchWhatsappThreads());
                threadFetchToken += 1;
                fetchToken = threadFetchToken;
                const THREADS_PAGE_LIMIT = 50;
                const modeParams = SEARCH_MODE_PARAMS[searchMode] || SEARCH_MODE_PARAMS[''];
                // No ordering param: the BE whitelists only the `latest_message_time`
                // annotation (whatsapp/views.py) to avoid a message-join fan-out and
                // silently ignores `ordering=whatsapp_messages__created_time` — so it
                // was pure noise on the wire. Threads come back latest-first by default.
                let params = `&limit=${THREADS_PAGE_LIMIT}&is_archived_included=true${modeParams}`;

                if (searchMode === "TAG" && searchTag?.length) {
                    params = `${params}&tagged_items__tag__name__in=${encodeURIComponent(searchTag)}`;
                }
                if (searchMode === "MY") {
                    params = `${params}&lead__assignees__user__id=${currentUser?.pk}`;
                }
                const baseUrl = `/api/v1/practices/${practiceId}/whatsapp-business-accounts/${currentBusinessId}/whatsapp-phones/${currentWhatsappPhoneId}/whatsapp-message-threads/`;
                let api = `${baseUrl}?${params}`;
                if (currentSearch?.length) {
                    // 2026-06-11 BUG FIX — search must find ANY conversation regardless
                    // of the active tab's mode filter. Previously the search URL inherited
                    // `params` (incl. campaign_or_pending or is_pending) which hid answered
                    // non-campaign threads. Build a minimal params string for search: only
                    // limit + ordering; is_archived_included lets deleted/archived threads
                    // surface if they match the query.
                    const searchParams = `limit=${THREADS_PAGE_LIMIT}&is_archived_included=true&ordering=whatsapp_messages__created_time&query=${encodeURIComponent(currentSearch)}`;
                    api = `${baseUrl}search/?${searchParams}`;
                }
                const threadResponse = await callApi(api, 'GET');
                // A newer fetch was dispatched while this one was in flight —
                // its result is the truth. Committing here is exactly how a
                // cleared search box kept showing the old search results.
                if (fetchToken !== threadFetchToken) return;
                threadMessages = threadResponse?.data?.results ?? [];
                threadNext = threadResponse?.data?.next ?? "";
                dispatch(slice.actions.completeFetchWhatsappThreads({
                    threadMessages,
                    threadNext
                }))
            }
        } catch (error) {
            console.error("Error in initialize Threads", error);
            // A superseded fetch must not toast or wipe the list that the
            // newer fetch is busy filling.
            if (fetchToken !== null && fetchToken !== threadFetchToken) return;
            // Carry the cause. Without it errorHandler.jsx captures a bare
            // Error with status/code/endpoint all null, which is why the
            // 2026-10-08 event said nothing and the wedge had to be found by
            // eliminating every other throw in this try block.
            dispatch(updateError({
                message: "Failed to get Whatsapp Messages",
                success: false,
                cause: error,
                diagnosticMessage: `getWhatsaAppThreads: ${error?.message || 'unknown error'}`,
            }));
            if (fetchNext) {
                dispatch(slice.actions.completeThreadNextFetch({
                    // undefined (not []) so the reducer's ?? preserves the
                    // existing list — [] is truthy and would wipe it.
                    threadMessages: undefined,
                    threadNext: ""
                }))
            } else {
                dispatch(slice.actions.completeFetchWhatsappThreads({
                    threadMessages: [],
                    threadNext: ""
                }))
            }
        }
    }
}

export function searchContactFromLeadRepo(searchKey) {
    return async () => {
        try {
            if (searchKey?.length) {
                dispatch(slice.actions.startLeadSearchResults());
                const practiceId = getPracticeId();
                const response = await callApi(
                    `/api/v1/practices/${practiceId}/leads/search/?query=${searchKey}&offset=0&ordering=-created_at`
                );
                dispatch(slice.actions.storeLeadSearchResults({ data: response?.data?.results ?? [] }))
            } else {
                dispatch(slice.actions.storeLeadSearchResults({ data: [] }))
            }
        } catch (error) {
            console.error("Error in searching leads", error);
            dispatch(updateError({ message: "Failed to search contacts, please try after sometime!", success: false }));
            dispatch(slice.actions.storeLeadSearchResults({ data: [] }));
        }
    }
}

export function fetchWhatsappChatAndOtherMetaData(urlThreadId = null) {
    return async () => {
        if (!hasFeatureAccess('whatsapp-messaging')) return;
        try {
            dispatch(slice.actions.startWhatsappChatLoad());
            const practiceId = getPracticeId();
            let state = store.getState()?.whatsappV2;
            const currentBusinessId = state?.currentBusinessId;
            const currentWhatsappPhoneId = state?.currentWhatsappPhoneId;

            // Guard: Don't make API calls if WhatsApp account/phone IDs are not initialized
            if (!currentBusinessId || currentBusinessId === -1 || !currentWhatsappPhoneId || currentWhatsappPhoneId === -1) {
                console.warn('fetchWhatsappChatAndOtherMetaData skipped: WhatsApp IDs not initialized');
                dispatch(slice.actions.errorWhatsappChatLoad());
                return;
            }

            // Stale-practice guard: IDs are valid but belong to a different practice.
            // Trigger re-init so the next fetchWhatsappChatAndOtherMetaData call
            // arrives with correct IDs rather than firing a cross-practice URL.
            // !isLoading guard matches getWhatsaAppThreads (avoids concurrent re-inits).
            // startInitializeWhatsapp is dispatched first (synchronous loading-flip)
            // to prevent re-entry; the reducer now preserves pieSocketSubscriptions
            // so initializeWhatsapp() can still unsubscribe old-practice sockets.
            const isLoading = state?.isLoading;
            const initializedForPracticeId = state?.initializedForPracticeId;
            if (
                !isLoading &&
                initializedForPracticeId !== null &&
                String(initializedForPracticeId) !== String(practiceId)
            ) {
                console.warn('fetchWhatsappChatAndOtherMetaData skipped: stale practiceId, re-initialising');
                dispatch(slice.actions.startInitializeWhatsapp());
                dispatch(initializeWhatsapp());
                dispatch(slice.actions.errorWhatsappChatLoad());
                return;
            }

            // Direct-load path: when invoked with an explicit URL threadId
            // (e.g. user pasted /whatsapp/thread/123 into the address bar
            // and the parent's thread-list-prefetch couldn't seed the chat
            // — different phone profile, closed/archived filtered out, slow
            // init, etc.), fetch the thread by id first and seed metadata
            // before continuing into the standard flow. Skip when metadata
            // already points at the same thread.
            const existingMetaThreadId = state?.whatsAppChatMetaData?.thread?.id;
            if (urlThreadId && String(existingMetaThreadId) !== String(urlThreadId)) {
                const threadResponse = await callApi(
                    `/api/v1/practices/${practiceId}/whatsapp-business-accounts/${currentBusinessId}/whatsapp-phones/${currentWhatsappPhoneId}/whatsapp-message-threads/${urlThreadId}/?is_archived_included=true`,
                    'GET'
                );
                if (threadResponse?.status === 200 && threadResponse?.data?.id) {
                    const fetchedThread = threadResponse.data;
                    const { avatar, name, phoneNumber, isCloseObject } = fetchContactDetails(fetchedThread);
                    dispatch(slice.actions.saveWhatsappChatMetaData({
                        leadId: fetchedThread?.lead?.id,
                        thread: fetchedThread,
                        name,
                        phoneNumber,
                        avatar,
                        isCloseObject,
                    }));
                    // Re-read state so the rest of the function sees the
                    // freshly seeded metadata.
                    state = store.getState()?.whatsappV2;
                } else {
                    dispatch(updateError({
                        message: "Failed to open chat - thread not found",
                        success: false,
                    }));
                    dispatch(slice.actions.errorWhatsappChatLoad());
                    return;
                }
            }

            const whatsAppChatMetaData = state?.whatsAppChatMetaData;
            const { leadId, thread } = whatsAppChatMetaData ?? {};
            if (leadId && thread) {
                dispatch(markAsRead(thread?.id, false));
                // Chat found in history and existing lead
                const whatsappChats = await callApi(
                    `/api/v1/practices/${practiceId}/whatsapp-business-accounts/${currentBusinessId}/whatsapp-phones/${currentWhatsappPhoneId}/whatsapp-message-threads/${thread?.id}/whatsapp-messages/?ordering=-created_time&is_archived_included=true`,
                    'GET'
                );
                // Getting lead Details
                let leadData = await callApi(
                    `/api/v1/practices/${practiceId}/leads/${leadId}/`,
                    'GET'
                );
                if (leadData?.status !== 200) {
                    leadData = await callApi(
                        `/api/v1/practices/${practiceId}/archived/${leadId}/`,
                        'GET'
                    );
                }
                const whatsappTags = await callApi(
                    `/api/v1/practices/${practiceId}/whatsapp-business-accounts/${currentBusinessId}/whatsapp-phones/${currentWhatsappPhoneId}/whatsapp-message-threads/${thread?.id}/tags/?is_archived_included=true`,
                    'GET'
                );
                dispatch(slice.actions.completeWhatsapChatLoad({
                    whatsappChats: whatsappChats?.data?.results ?? [],
                    whatsAppChatNextAPI: whatsappChats?.data?.next,
                    whatsAppChatMetaData: {
                        ...whatsAppChatMetaData,
                        // `?? {}` is load-bearing: a non-200 lead GET (e.g. a
                        // cross-tenant/deleted lead on a coexistence thread →
                        // 403/404, archived fallback also failing) leaves
                        // leadData.data undefined. ChatPage's "backfill missing
                        // leadDetails" effect re-fires while `!leadDetails`, so
                        // an undefined here = infinite re-dispatch → page freeze.
                        // Empty object means "fetch attempted, no lead" and
                        // breaks the loop (matches the other branches below).
                        leadDetails: leadData?.data ?? {},
                        // Close state travels on the thread (is_closed /
                        // metadata.close_reason) but ChatPage reads it from
                        // `isCloseObject`, which only the Contacts / thread-URL
                        // paths used to set — so the SAME resolved thread showed
                        // "Resolved" + Reopen in the inbox and neither inside the
                        // follow-up popup. Derive it here, exactly as the
                        // direct-load path above does.
                        isCloseObject: fetchContactDetails(thread)?.isCloseObject,
                        tags: whatsappTags?.data?.results ?? []
                    }
                }));
            }
            else if (!leadId && thread) {
                dispatch(markAsRead(thread?.id, false));
                // Chat found in history but is a new lead
                let whatsappMessages = {}
                if (thread?.id) {
                    whatsappMessages = await callApi(
                        `/api/v1/practices/${practiceId}/whatsapp-business-accounts/${currentBusinessId}/whatsapp-phones/${currentWhatsappPhoneId}/whatsapp-message-threads/${thread?.id}/whatsapp-messages/?ordering=-created_time&is_archived_included=true`,
                        'GET'
                    );
                }
                const whatsappTags = await callApi(
                    `/api/v1/practices/${practiceId}/whatsapp-business-accounts/${currentBusinessId}/whatsapp-phones/${currentWhatsappPhoneId}/whatsapp-message-threads/${thread?.id}/tags/?is_archived_included=true`,
                    'GET'
                );
                dispatch(slice.actions.completeWhatsapChatLoad({
                    whatsappChats: whatsappMessages?.data?.results ?? [],
                    whatsAppChatNextAPI: whatsappMessages?.data?.next,
                    whatsAppChatMetaData: {
                        ...whatsAppChatMetaData,
                        leadDetails: {},
                        isCloseObject: fetchContactDetails(thread)?.isCloseObject,
                        tags: whatsappTags?.data?.results ?? []
                    }
                }));
            } else if (leadId && !thread) {
                // Chat might be in history but is an existing lead.
                // Use exact lead_id filter (NOT fuzzy phone search) to find the
                // thread — fuzzy search matches wrong threads when two leads
                // share similar phone number prefixes.
                let leadData = await callApi(
                    `/api/v1/practices/${practiceId}/leads/${leadId}/`,
                    'GET'
                );
                if (leadData?.status !== 200) {
                    leadData = await callApi(
                        `/api/v1/practices/${practiceId}/archived/${leadId}/`,
                        'GET'
                    );
                }
                // Find thread by exact lead FK — no ambiguity possible
                const threadResponse = await callApi(
                    `/api/v1/practices/${practiceId}/whatsapp-business-accounts/${currentBusinessId}/whatsapp-phones/${currentWhatsappPhoneId}/whatsapp-message-threads/?lead=${leadId}&is_archived_included=true&ordering=-created_at`,
                    'GET'
                );
                let matchedThread = threadResponse?.data?.results?.[0];

                // Fallback: if no thread linked by lead FK, look up an
                // ORPHAN thread by EXACT phone number (for leads created
                // before thread-lead linking was implemented).
                //
                // DO NOT use /whatsapp-message-threads/search/?query=...
                // here — that endpoint uses Postgres trigram similarity
                // (>0.4) across name/email/phone/body and orders by
                // similarity. results[0] is effectively arbitrary; any
                // orphan thread whose name or body trigram-matched the
                // current lead's phone digits leaks into the wrong lead's
                // tab (proven: lead 170010 "Darcie Sutton" got Nikki
                // brazier's Sept-2024 chat). Always use the exact
                // to_waid filter combined with lead__isnull=true.
                if (!matchedThread) {
                    let phoneNumber = leadData?.data?.lead_contact?.phone;
                    phoneNumber = phoneNumber?.trim().replaceAll(" ", "").replaceAll("+", "");
                    if (phoneNumber) {
                        const fallbackResponse = await callApi(
                            `/api/v1/practices/${practiceId}/whatsapp-business-accounts/${currentBusinessId}/whatsapp-phones/${currentWhatsappPhoneId}/whatsapp-message-threads/?to_waid=${phoneNumber}&lead__isnull=true&is_archived_included=true&ordering=-created_at&limit=1`,
                            'GET'
                        );
                        // Belt-and-braces: still require the result to be
                        // orphan even though lead__isnull=true already
                        // enforces it on the BE.
                        const fallbackThread = fallbackResponse?.data?.results?.[0];
                        if (fallbackThread && !fallbackThread.lead?.id) {
                            matchedThread = fallbackThread;
                        }
                    }
                }

                const foundThread = matchedThread ?? {};
                let whatsappMessages = {}
                if (foundThread?.id) {
                    whatsappMessages = await callApi(
                        `/api/v1/practices/${practiceId}/whatsapp-business-accounts/${currentBusinessId}/whatsapp-phones/${currentWhatsappPhoneId}/whatsapp-message-threads/${foundThread?.id}/whatsapp-messages/?ordering=-created_time&is_archived_included=true`,
                        'GET'
                    );
                }
                dispatch(slice.actions.completeWhatsapChatLoad({
                    whatsappChats: whatsappMessages?.data?.results ?? [],
                    whatsAppChatNextAPI: whatsappMessages?.data?.next,
                    whatsAppChatMetaData: {
                        ...whatsAppChatMetaData,
                        thread: foundThread,
                        // Close state travels on the thread (is_closed /
                        // metadata.close_reason) but ChatPage reads it from
                        // `isCloseObject`, which only the Contacts / thread-URL
                        // paths used to set — so the SAME resolved thread showed
                        // "Resolved" + Reopen in the inbox and neither inside the
                        // follow-up popup. Derive it here, exactly as the
                        // direct-load path above does.
                        isCloseObject: fetchContactDetails(foundThread)?.isCloseObject,
                        // See note above — never let leadDetails be undefined,
                        // or ChatPage's backfill effect loops → freeze.
                        leadDetails: leadData?.data ?? {}
                    }
                }));
            } else {
                console.error("No Lead id or Thread Found");
                dispatch(slice.actions.errorWhatsappChatLoad());
            }
        } catch (error) {
            console.error("Error in getting details for this lead", error);
            dispatch(updateError({ message: "Failed to get chats, please try after sometime", success: false }));
            dispatch(slice.actions.errorWhatsappChatLoad());
        }
    }
}

/**
 * Assemble the WhatsApp send `template_context` with the correct precedence:
 * auto-fetched practice → lead → treatment context, then the chip-key aliases,
 * then the logged-in USER's sender context (user_first_name — mirrors the email
 * `user_first_name` mechanism, sourced from /auth/user/get_context/ via
 * state.mergeTags.mtValUser), and finally caller overrides (e.g. the Personal
 * Reply answer slot) which always win.
 *
 * Spreading the full userCtx is safe for ALL sends: the mapping-scoped BE
 * validator ignores keys not in a template's mapping, and unused keys never
 * reach Meta via generate_template_params.
 *
 * @returns {object} the merged template_context
 */
export function buildWhatsappSendContext({ practiceCtx, leadCtx, treatmentCtx, aliases, userCtx, extraContext } = {}) {
    return {
        ...(practiceCtx || {}),
        ...(leadCtx || {}),
        ...(treatmentCtx || {}),
        ...(aliases || {}),
        ...(userCtx || {}),
        ...(extraContext || {}),
    };
}

export function sendWhatsappMessage(message, messageType, callbackFnc, { onSuccess, onFailure, repliedToWamid, extraContext = {} } = {}) {
    return async () => {
        const practiceId = getPracticeId();
        const state = store.getState()?.whatsappV2;
        const { currentBusinessId, currentWhatsappPhoneId, whatsAppChatMetaData } = state;

        // Guard: Don't make API calls if WhatsApp account/phone IDs are not initialized
        if (!hasConnectedWhatsappWriteContext(state)) {
            console.warn('sendWhatsappMessage skipped: WhatsApp IDs not initialized');
            dispatch(updateError({ message: "WhatsApp is still loading, please try again", success: "warning" }));
            return;
        }

        // Synchronous latch keyed by (thread, body). Closes before any await
        // so a held-Enter / keyboard auto-repeat second invocation 18-30 ms
        // later short-circuits before it can race the BE.
        const inflightKey = `${whatsAppChatMetaData?.thread?.id ?? 'new'}|${messageType ?? 'text'}|${(message ?? '').slice(0, 256)}`;
        if (waSendInflightKeys.has(inflightKey)) {
            return;
        }
        waSendInflightKeys.add(inflightKey);
        // BE idempotency key — same UUID per logical Send click. BE caches
        // for 60s and replays the original message on a duplicate POST.
        const clientMessageId = newClientMessageId();

        const data = {};
        const threadId = whatsAppChatMetaData?.thread?.id;
        const leadId = whatsAppChatMetaData?.leadDetails?.id;
        const threadLeadId = whatsAppChatMetaData?.thread?.lead?.id;  // Lead ID from thread metadata
        let phoneNumberToSend = whatsAppChatMetaData?.phoneNumber;
        try {
            dispatch(slice.actions.toggleWhatsappSendLoading(true));
            // Patch phone number if needed
            if (phoneNumberToSend?.length === 10 && leadId) {
                phoneNumberToSend = `44${phoneNumberToSend}`;
                await callApi(`/api/v1/practices/${practiceId}/leads/${leadId}/`, 'PATCH', {
                    id: leadId,
                    lead_contact: { phone: phoneNumberToSend }
                });
            }

            data.message_type = messageType ?? 'text';
            data.to = phoneNumberToSend;
            if (messageType === 'template') data.template_name = message;
            else data.message = message;
            // GDPR FIX: Always include lead_id if thread has a linked lead
            // Use leadDetails.id if available, otherwise fall back to thread.lead.id
            // This ensures GDPR checks run even if leadDetails hasn't loaded yet
            const effectiveLeadId = leadId || threadLeadId;
            if (effectiveLeadId) data.lead_id = effectiveLeadId;
            // Reply-to-quote: when the operator clicked Reply on a bubble,
            // forward the parent wamid so the backend embeds Meta's
            // ``context.message_id`` and the message renders as a threaded
            // reply on the patient's device.
            if (repliedToWamid) data.replied_to_wamid = repliedToWamid;
            data.client_message_id = clientMessageId;

            // Parallel fetch template context
            if (messageType === 'template') {
                const [practiceCtxRes, leadCtxRes, treatmentsRes] = await Promise.all([
                    callApi(`/api/v1/practices/${practiceId}/get_context/`, 'GET'),
                    callApi(`/api/v1/practices/${practiceId}/leads/${leadId}/get_context/`, 'GET'),
                    callApi(`/api/v1/practices/${practiceId}/leads/${leadId}/treatments/`)
                ]);

                const treatmentIds = treatmentsRes?.data?.results?.map(t => t?.id) ?? [];

                const treatmentContexts = await Promise.all(
                    treatmentIds.map(id =>
                        callApi(`/api/v1/practices/${practiceId}/leads/${leadId}/treatments/${id}/get_context/`)
                    )
                );

                const treatmentContextCombined = Object.assign({}, ...treatmentContexts.map(t => t?.data ?? {}));

                // 2026-06-12 FIX — merge-tag key mismatch.
                // Template chips (built in RequestTemplateDialog / buildMergeTags)
                // emit short keys: first_name, last_name, practice_address.
                // The context endpoints return canonical keys: lead_first_name,
                // lead_last_name, practice_full_address.
                // The BE substitutes via context.get(key) so context.get('first_name')
                // → '' → blank name on every template that uses a chip.
                // Fix: add alias keys after the spread so chip keys resolve.
                // Guard each alias: only set it when the source is defined so we
                // never clobber a real key with undefined.
                // NOTE: lead_first_name / practice_full_address are preserved in the
                // spread above — templates that were already mapped to the canonical
                // keys (e.g. windsor_open_day) continue to work unchanged.
                // TODO (follow-up): buildMergeTags in RequestTemplateDialog.jsx
                // emits first_name/practice_address — those chips should arguably
                // emit the canonical keys so aliases are not needed here. Tracked
                // for a separate PR once the alias is confirmed working.
                const mergeTagAliases = {
                    ...(leadCtxRes?.data?.lead_first_name !== undefined && {
                        first_name: leadCtxRes.data.lead_first_name,
                    }),
                    ...(leadCtxRes?.data?.lead_last_name !== undefined && {
                        last_name: leadCtxRes.data.lead_last_name,
                    }),
                    ...(practiceCtxRes?.data?.practice_full_address !== undefined && {
                        practice_address: practiceCtxRes.data.practice_full_address,
                    }),
                };
                // Logged-in user's sender context (user_first_name etc.) — already
                // loaded on dashboard mount from /auth/user/get_context/. Spread so
                // the WhatsApp "Your name" merge tag resolves to the sender, exactly
                // like the email mechanism. Safe unconditionally (mapping-scoped BE
                // validator ignores keys a template doesn't use).
                const userCtx = store.getState()?.mergeTags?.mtValUser ?? {};
                data.template_context = buildWhatsappSendContext({
                    practiceCtx: practiceCtxRes?.data,
                    leadCtx: leadCtxRes?.data,
                    treatmentCtx: treatmentContextCombined,
                    aliases: mergeTagAliases,
                    userCtx,
                    // Caller-supplied overrides (e.g. Personal Reply answer slot)
                    // applied LAST so they win over auto-fetched context values.
                    extraContext,
                });
            }

            // Send final message.
            //
            // `suppressServerErrorDispatch` keeps apiCall's generic
            // "Something went wrong! (<endpoint>)" toast off the screen: a failed
            // send needs ONE specific message, and on a 5xx we may not even know
            // that it failed. Sentry JAVASCRIPT-REACT-13E showed that generic
            // toast stacked on top of "Failed to send WhatsApp message. Please
            // try again." for a message the patient had already received.
            const sendUrl = `/api/v1/practices/${practiceId}/whatsapp-business-accounts/${currentBusinessId}/whatsapp-phones/${currentWhatsappPhoneId}/send_message/`;
            const sendOptions = { suppressServerErrorDispatch: true };
            let response = await callApi(sendUrl, 'POST', data, false, sendOptions);

            // A 5xx is an unknown outcome, not a failure — so resolve it instead
            // of guessing. Re-POST once with the SAME `client_message_id`: the
            // backend caches that id per phone for 60s and REPLAYS the original
            // WhatsappMessage rather than calling Meta again, so this tells us
            // whether the first attempt landed with no risk of a duplicate.
            // Deliberately limited to a real 5xx RESPONSE — on a timeout the
            // first request may still be in flight server-side, where the
            // idempotency slot still reads PENDING and a re-POST WOULD send a
            // second message.
            if (shouldRecheckSend(response)) {
                response = await callApi(sendUrl, 'POST', data, false, sendOptions);
            }

            // The idempotent-replay response is a DIFFERENT shape: `thread` is the
            // thread id (an int), not the serialized thread object, because no new
            // send happened. Normalise it here - the success path below would
            // otherwise push a bare number into the thread list.
            const isIdempotentReplay = response?.data?.idempotent_replay === true;
            const threadData = isIdempotentReplay ? null : response?.data?.thread;
            const resId = isIdempotentReplay
                ? response?.data?.thread
                : response?.data?.thread?.id;

            dispatch(slice.actions.toggleWhatsappSendLoading(false));

            // Optimization: Use fresh data from send_message response instead of immediate refresh
            // Backend now returns updated contacted_count and treatment data synchronously
            if (response?.status === 200 && isIdempotentReplay) {
                // The first attempt DID reach Meta — only its confirmation was
                // lost. Treat this as sent: no retry prompt, no restored draft,
                // just refetch so the real bubble replaces the optimistic one.
                onSuccess?.();
                dispatch(refreshThreadAndMessageInfo(threadId ?? resId, leadId));
                fetchFilterCounts(false);
                dispatch(updateError({
                    message: 'Message sent \u2014 confirmation was delayed.',
                    success: true,
                    timeout: 4000,
                }));
            } else if (response?.status === 200 && threadData) {
                onSuccess?.();
                // Update thread list with fresh data
                const currentThreads = getWhatsappThreadsFromStore() ?? [];
                const filtered = currentThreads.filter((t) => `${t.id}` !== `${threadData.id ?? -1}`);
                const updatedThreads = [threadData, ...filtered];
                dispatch(slice.actions.saveThreadDetails(updatedThreads));

                // Update chat metadata if this is the active conversation
                if ((`${threadId}` === `${threadData.id}`) || (!threadId && resId)) {
                    // Conditionally fetch fresh lead data only if a fallback treatment was created
                    // This reduces unnecessary API calls when no treatment changes occurred
                    const fallbackTreatmentCreated = response?.data?.fallback_treatment_created ?? false;
                    if (leadId && fallbackTreatmentCreated) {
                        const leadResponse = await callApi(`/api/v1/practices/${practiceId}/leads/${leadId}/`, 'GET');
                        if (leadResponse?.status === 200) {
                            dispatch(slice.actions.saveLeadDetails({
                                ...whatsAppChatMetaData,
                                leadDetails: leadResponse?.data ?? {},
                            }));
                        }
                    }

                    // Refresh messages to show the newly sent message
                    const messagesResponse = await callApi(
                        `/api/v1/practices/${practiceId}/whatsapp-business-accounts/${currentBusinessId}/whatsapp-phones/${currentWhatsappPhoneId}/whatsapp-message-threads/${threadId ?? resId}/whatsapp-messages/?ordering=-created_time&is_archived_included=true`,
                        'GET'
                    );
                    if (messagesResponse?.status === 200) {
                        dispatch(slice.actions.saveMessageDetails(messagesResponse?.data?.results ?? []));
                    }
                }

                // Update filter counts
                fetchFilterCounts(false);
            } else if (response?.status === 403) {
                // GDPR compliance: lead is not open for communication
                onFailure?.();
                dispatch(updateError({
                    message: "This lead is not available for communication. They may have been archived or opted out.",
                    success: false,
                    timeout: 5000
                }));
            } else if (response?.status === 409 && response?.data?.code === 'service_window_closed') {
                // PMP rules — Meta will not deliver free-form messages outside
                // the 24-hour service window. Show a structured dialog (not a
                // toast — the operator needs the "switch to template" CTA in
                // a sticky surface, not a dismissable corner pop) so they
                // can act without losing the typed draft.
                onFailure?.();
                dispatch(slice.actions.showServiceWindowClosedDialog({
                    error: response?.data?.error
                        || "WhatsApp service window is closed. Send an approved template instead.",
                    serviceWindow: response?.data?.service_window || null,
                    draft: messageType === 'template' ? null : message,
                }));
            } else if (
                response?.status === 409
                && String(response?.data?.code || '').startsWith('audience_')
            ) {
                // Template audience rules refused this recipient. The picker
                // already flags these inline with the full reason, so reaching
                // here means a stale client or a direct call — a short, calm
                // toast is the right weight. This is the fail-safe, not the
                // primary explanation.
                onFailure?.();
                dispatch(updateError({
                    message: shortAudienceReason(response?.data?.code),
                    success: false,
                    timeout: 4000,
                }));
            } else if (isUnconfirmedSendOutcome(response)) {
                // Server error (twice, including the idempotent re-check) or a
                // timed-out request. We still cannot promise the message did not
                // go out, so never say "try again": restore the draft and put the
                // operator in front of the conversation to judge for themselves.
                onFailure?.();
                dispatch(refreshThreadAndMessageInfo(threadId, leadId));
                dispatch(updateError({
                    message: UNCONFIRMED_SEND_MESSAGE,
                    success: false,
                    timeout: 8000,
                }));
            } else if (!threadId && callbackFnc && !resId) {
                onFailure?.();
                dispatch(updateError({
                    message: "Please reopen latest thread to view messages!",
                    success: "warning"
                }));
                callbackFnc?.();
            } else if (response?.status !== 200) {
                // Show the actual Meta API error from our backend (e.g. "Template paused",
                // "Rate limit exceeded", "Number not on WhatsApp") instead of a generic message.
                // Backend returns { success: false, error: "Failed to send message: <detail>" }
                onFailure?.();
                const apiError = sendRefusalReason(response);
                dispatch(updateError({
                    message: apiError || "Failed to send WhatsApp message. Please try again.",
                    success: false,
                    timeout: 8000,
                }));
            }

            dispatch(markAsRead(threadId ?? resId, false));
        } catch (error) {
            console.error("Error in sending Whatsapp Message", error);
            onFailure?.();
            dispatch(updateError({
                message: "Failed to send WhatsApp message. Please check your connection and try again.",
                success: false,
            }));
            dispatch(slice.actions.toggleWhatsappSendLoading(false));
            dispatch(refreshThreadAndMessageInfo(threadId, leadId));
        } finally {
            // Always release the latch — including on error — so the operator
            // can retry a failed send without a hard reload.
            waSendInflightKeys.delete(inflightKey);
        }
    };
}

// Track D / F3 — Forward N messages to one target thread.
// Calls the BE forward-messages endpoint, then refreshes the target
// thread's message list so the forwarded messages appear with the
// "Forwarded" attribution label. Returns the API response so the caller
// can decide whether to navigate to the target thread on success.
export function forwardMessages({ messageIds, targetThreadId, onSuccess, onFailure } = {}) {
    return async () => {
        const practiceId = getPracticeId();
        const state = store.getState()?.whatsappV2;
        const { currentBusinessId, currentWhatsappPhoneId, whatsAppChatMetaData } = state;

        if (!hasConnectedWhatsappWriteContext(state)) {
            dispatch(updateError({
                message: 'WhatsApp is still loading, please try again',
                success: 'warning',
            }));
            onFailure?.();
            return null;
        }
        if (!Array.isArray(messageIds) || !messageIds.length || !targetThreadId) {
            dispatch(updateError({
                message: 'Pick at least one message and a target thread to forward',
                success: 'warning',
            }));
            onFailure?.();
            return null;
        }

        try {
            const response = await callApi(
                `/api/v1/practices/${practiceId}/whatsapp-business-accounts/${currentBusinessId}/whatsapp-phones/${currentWhatsappPhoneId}/forward-messages/`,
                'POST',
                { message_ids: messageIds, target_thread_id: targetThreadId },
            );

            if (response?.status === 200 && response?.data?.success) {
                const forwardedCount = response?.data?.forwarded?.length || 0;
                const skipped = response?.data?.errors?.length || 0;
                dispatch(updateError({
                    message: skipped
                        ? `Forwarded ${forwardedCount}, ${skipped} skipped`
                        : `Forwarded ${forwardedCount} message${forwardedCount === 1 ? '' : 's'}`,
                    success: skipped ? 'warning' : true,
                }));

                // Optimistic refresh — only refresh the target thread's
                // chat panel if it's the one currently open. Otherwise no
                // op (the operator will see them next time they open it).
                const openThreadId = whatsAppChatMetaData?.thread?.id;
                if (`${openThreadId}` === `${targetThreadId}`) {
                    dispatch(refreshThreadAndMessageInfo(targetThreadId, null, true));
                }

                onSuccess?.(response.data);
                return response.data;
            }

            // service_window_closed → 409 with a code, surface as warning
            if (response?.status === 409 && response?.data?.error === 'service_window_closed') {
                dispatch(updateError({
                    message: response?.data?.message
                        || 'Cannot forward: the 24-hour customer service window for the target chat is closed.',
                    success: 'warning',
                    timeout: 8000,
                }));
                onFailure?.(response.data);
                return response.data;
            }

            const apiError = response?.data?.error || response?.data?.message;
            dispatch(updateError({
                message: apiError || 'Failed to forward messages. Please try again.',
                success: false,
                timeout: 8000,
            }));
            onFailure?.(response?.data);
            return response?.data ?? null;
        } catch (err) {
            console.error('forwardMessages error', err);
            dispatch(updateError({
                message: 'Failed to forward messages. Please check your connection and try again.',
                success: false,
            }));
            onFailure?.();
            return null;
        }
    };
}

export function sendWhatsappMediaMessage(file, caption) {
    return async () => {
        const practiceId = getPracticeId();
        const state = store.getState()?.whatsappV2;
        const { currentBusinessId, currentWhatsappPhoneId, whatsAppChatMetaData } = state;

        // Guard: Don't make API calls if WhatsApp account/phone IDs are not initialized
        if (!hasConnectedWhatsappWriteContext(state)) {
            console.warn('sendWhatsappMediaMessage skipped: WhatsApp IDs not initialized');
            dispatch(updateError({ message: "WhatsApp is still loading, please try again", success: "warning" }));
            return;
        }

        const threadId = whatsAppChatMetaData?.thread?.id;
        const leadId = whatsAppChatMetaData?.leadDetails?.id;
        const threadLeadId = whatsAppChatMetaData?.thread?.lead?.id;
        const phoneNumberToSend = whatsAppChatMetaData?.phoneNumber;

        try {
            dispatch(slice.actions.toggleWhatsappSendLoading(true));

            // Step 1: Upload the file
            const formData = new FormData();
            formData.append('file', file);

            const uploadResponse = await callApi(
                `/api/v1/practices/${practiceId}/whatsapp-business-accounts/${currentBusinessId}/whatsapp-phones/${currentWhatsappPhoneId}/upload_media/`,
                'POST',
                formData
            );

            if (!uploadResponse?.ok) {
                dispatch(updateError({ message: "Failed to upload file, please try again", success: false }));
                dispatch(slice.actions.toggleWhatsappSendLoading(false));
                return;
            }

            const { url: mediaUrl, filename: mediaFilename, mime_type: mimeType } = uploadResponse.data;

            // Step 2: Determine message type from mime_type
            let messageType = 'document';
            if (mimeType && mimeType.includes('image')) messageType = 'image';
            else if (mimeType && mimeType.includes('video')) messageType = 'video';

            // Step 3: Send the message with media
            const data = {
                message_type: messageType,
                to: phoneNumberToSend,
                media_url: mediaUrl,
                media_mime_type: mimeType,
            };

            if (caption) data.media_caption = caption;
            if (messageType === 'document') data.media_filename = mediaFilename;

            // GDPR FIX: Always include lead_id if thread has a linked lead
            const effectiveLeadId = leadId || threadLeadId;
            if (effectiveLeadId) data.lead_id = effectiveLeadId;

            const response = await callApi(
                `/api/v1/practices/${practiceId}/whatsapp-business-accounts/${currentBusinessId}/whatsapp-phones/${currentWhatsappPhoneId}/send_message/`,
                'POST',
                data
            );

            const resId = response?.data?.thread?.id;
            const threadData = response?.data?.thread;

            dispatch(slice.actions.toggleWhatsappSendLoading(false));

            if (response?.status === 200 && threadData) {
                // Update thread list with fresh data
                const currentThreads = getWhatsappThreadsFromStore() ?? [];
                const filtered = currentThreads.filter((t) => `${t.id}` !== `${threadData.id ?? -1}`);
                const updatedThreads = [threadData, ...filtered];
                dispatch(slice.actions.saveThreadDetails(updatedThreads));

                // Update chat metadata if this is the active conversation
                if ((`${threadId}` === `${threadData.id}`) || (!threadId && resId)) {
                    // Refresh messages to show the newly sent message
                    const messagesResponse = await callApi(
                        `/api/v1/practices/${practiceId}/whatsapp-business-accounts/${currentBusinessId}/whatsapp-phones/${currentWhatsappPhoneId}/whatsapp-message-threads/${threadId ?? resId}/whatsapp-messages/?ordering=-created_time&is_archived_included=true`,
                        'GET'
                    );
                    if (messagesResponse?.status === 200) {
                        dispatch(slice.actions.saveMessageDetails(messagesResponse?.data?.results ?? []));
                    }
                }

                // Update filter counts
                fetchFilterCounts(false);
            } else if (response?.status === 403) {
                dispatch(updateError({
                    message: "This lead is not available for communication. They may have been archived or opted out.",
                    success: false,
                    timeout: 5000
                }));
            } else if (response?.status !== 200) {
                // Show actual Meta API error for media sends too (same pattern as text sends)
                const apiError = response?.data?.error || response?.data?.message;
                dispatch(updateError({
                    message: apiError || "Failed to send media message. Please try again.",
                    success: false,
                    timeout: 8000,
                }));
            }

            dispatch(markAsRead(threadId ?? resId, false));
        } catch (error) {
            console.error("Error in sending WhatsApp media message", error);
            dispatch(updateError({
                message: "Failed to send media message, please try again!",
                success: "warning"
            }));
            dispatch(slice.actions.toggleWhatsappSendLoading(false));
            dispatch(refreshThreadAndMessageInfo(threadId, leadId));
        }
    };
}

const removeCurrentFromThreadAndUpdateInfo = async (currentThreads, threadId, refreshMessageDetails, whatsAppChatMetaData) => {
    try {
        const practiceId = getPracticeId();
        const state = store.getState()?.whatsappV2;
        const { currentBusinessId, currentWhatsappPhoneId } = state;

        // Guard: Don't make API calls if WhatsApp account/phone IDs are not set
        if (!currentBusinessId || !currentWhatsappPhoneId) {
            console.warn('WhatsApp removeCurrentFromThread skipped: currentBusinessId or currentWhatsappPhoneId is undefined');
            return;
        }

        let updated;
        const response = await callApi(
            threadDetailUrl(practiceId, currentBusinessId, currentWhatsappPhoneId, threadId),
            'GET'
        );
        if (response?.status === 200) {
            const threadData = response?.data;
            // Removing current thread
            const filtered = currentThreads.filter((t) => `${t.id}` !== `${threadData.id ?? -1}`);
            updated = [...filtered];
            dispatch(slice.actions.saveThreadDetails(updated));
            console.error("Current filters did not support current thread");
            const isCloseObject = {
                isClosed: threadData?.is_closed,
                closedReason: threadData?.metadata?.close_reason
            }
            if (refreshMessageDetails) {
                dispatch(slice.actions.refreshTagInfo({
                    whatsAppChatMetaData: {
                        ...whatsAppChatMetaData,
                        isCloseObject: isCloseObject ?? {}
                    }
                }));
            }
        } else if (response?.status === 404) {
            // Thread no longer exists server-side — that's exactly what this reconcile does.
            // Drop it from the local list silently; not an error.
            const filtered = currentThreads.filter((t) => `${t.id}` !== `${threadId}`);
            dispatch(slice.actions.saveThreadDetails([...filtered]));
        } else if (response?.status === 0) {
            // ISS-016 — status 0 is NOT a failure the user can act on: it means
            // no response arrived (request aborted on navigation/unmount, timed
            // out, offline, or blocked by the path guard at apiCall.js:233).
            // This branch used to toast, so staff got "please reload" every time
            // a patient replied — nine times in eleven minutes in the reported
            // session. Warning fatigue on a banner that means nothing.
            console.warn('[whatsappV2] thread reconcile got no response; skipping toast', { threadId });
        } else {
            // Genuine failure the server reported (4xx/5xx) — still worth saying.
            dispatch(updateError({ message: "Couldn't refresh this conversation — please reload.", success: "warning" }));
        }
    } catch (err) {
        // ISS-016 — an exception here is the same class of transient as status 0,
        // so no reload banner. But suppressing the toast must not also suppress
        // the signal: report it as a real exception so a genuine programming
        // error in the 200 branch above still reaches Sentry instead of dying in
        // a console.warn nobody reads.
        Sentry.captureException(err, {
            tags: { feature: 'whatsapp_thread_reconcile' },
            extra: { threadId },
        });
        console.warn('[whatsappV2] thread reconcile threw; skipping toast', err);
    }
}

// `options.background`: set by the socket listener (layouts/dashboard), where
// the refresh is not a user action, so the count refresh is throttled.
export function refreshThreadAndMessageInfo(threadId, leadId, refreshMessageDetails = true, patchThreadWithLeadInfo = false, newChat = false, options = {}) {
    return async () => {
        try {
            const practiceId = getPracticeId();
            const state = store.getState()?.whatsappV2;
            const { currentBusinessId, currentWhatsappPhoneId, whatsAppChatMetaData, searchMode, currentTag: searchTag, currentUser } = state;

            // Guard: Don't make API calls if WhatsApp account/phone IDs are not set
            if (!hasValidWhatsappContext(currentBusinessId, currentWhatsappPhoneId)) {
                console.warn('WhatsApp refresh skipped: currentBusinessId or currentWhatsappPhoneId is undefined');
                return;
            }

            // Filter context only; the archived-threads flag comes from threadDetailUrl.
            let params = '';

            if (searchMode === "TAG" && searchTag?.length) {
                params = `${params}&tagged_items__tag__name__in=${searchTag}`
            }
            if (searchMode === "CLOSED") {
                params = `${params}&is_closed=${true}`
            } else {
                params = `${params}&is_closed=${false}`
            }
            if (searchMode === "MY") {
                params = `${params}&lead__assignees__user__id=${currentUser?.pk}`
            }

            let whatsAppChatMetaDataClone = window.structuredClone(whatsAppChatMetaData);

            // Is `threadId` the conversation the operator currently has open?
            //
            // This thunk is dispatched for EVERY inbound message on the phone
            // (layouts/dashboard/index.jsx, the `new-whatsapp-message` listener),
            // not just for the open chat. The messages and tags fetches below
            // have always been gated on this; the `whatsAppChatMetaData` merge
            // further down was not, so a message landing on thread B rewrote the
            // open thread A's metadata with B's id/phone/service-window. That
            // churned `whatsAppChatMetaData` identity on every inbound message —
            // which force-scrolled ChatPage's message pane back to the bottom,
            // and, on a /whatsapp/thread/:threadId URL, made the metadata look
            // stale so ChatPage re-fetched the whole chat and swapped the
            // scroll container for a skeleton, blurring the composer mid-type.
            //
            // The thread LIST updates below are deliberately left ungated: the
            // sidebar must stay live for every thread. Only the open chat's own
            // metadata is protected here.
            const isOpenThread = `${threadId}` === `${whatsAppChatMetaData?.thread?.id}` || newChat;

            if (patchThreadWithLeadInfo) {
                await callApi(
                    threadDetailUrl(practiceId, currentBusinessId, currentWhatsappPhoneId, threadId),
                    'PATCH', {
                    lead: leadId
                }
                )
            }

            const messagePromise = refreshMessageDetails && isOpenThread
                ? callApi(
                    `/api/v1/practices/${practiceId}/whatsapp-business-accounts/${currentBusinessId}/whatsapp-phones/${currentWhatsappPhoneId}/whatsapp-message-threads/${threadId}/whatsapp-messages/?ordering=-created_time&is_archived_included=true`,
                    'GET'
                )
                : null;

            const whatsappTags = refreshMessageDetails && isOpenThread
                ? callApi(
                    `/api/v1/practices/${practiceId}/whatsapp-business-accounts/${currentBusinessId}/whatsapp-phones/${currentWhatsappPhoneId}/whatsapp-message-threads/${threadId}/tags/?is_archived_included=true`,
                    'GET'
                )
                : null;

            const threadPromise = threadId
                ? callApi(
                    threadDetailUrl(practiceId, currentBusinessId, currentWhatsappPhoneId, threadId, params.replace(/^&/, '')),
                    'GET'
                )
                : null;

            const leadPromise = leadId
                ? callApi(`/api/v1/practices/${practiceId}/leads/${leadId}/`, 'GET')
                : null;

            // Handle messages response independently
            if (messagePromise) {
                messagePromise
                    .then((res) => {
                        if (res?.status === 200)
                            dispatch(slice.actions.saveMessageDetails(res?.data?.results ?? []));
                        else
                            dispatch(updateError({
                                message: "Failed to get whatsapp messages !",
                                success: "warning"
                            }));
                    })
                    .catch((error) => {
                        console.error("Error fetching messages:", error);
                    });
            }

            // Handle Tags
            if (whatsappTags) {
                whatsappTags
                    .then((res) => {
                        if (res?.status === 200) {
                            whatsAppChatMetaDataClone = {
                                ...whatsAppChatMetaDataClone,
                                tags: res?.data?.results ?? []
                            }
                            dispatch(slice.actions.refreshTagInfo({
                                whatsAppChatMetaData: {
                                    ...whatsAppChatMetaDataClone
                                }
                            }));
                        }
                        else
                            dispatch(updateError({
                                message: "Failed to get whatsapp tags !",
                                success: "warning"
                            }));
                    })
                    .catch((error) => {
                        console.error("Error fetching messages:", error);
                    });
            }

            // Handle thread response independently
            if (threadPromise) {
                threadPromise
                    .then((res) => {
                        const threadData = res?.data;
                        const currentThreads = getWhatsappThreadsFromStore() ?? [];
                        const isCloseObject = {
                            isClosed: threadData?.is_closed,
                            closedReason: threadData?.metadata?.close_reason
                        }
                        if (refreshMessageDetails && isOpenThread && threadData?.id) {
                            whatsAppChatMetaDataClone = {
                                ...whatsAppChatMetaDataClone,
                                isCloseObject: isCloseObject ?? {},
                                // ISS-025 — refresh the cached thread object,
                                // not just isCloseObject. `whatsAppChatMetaData.thread`
                                // was previously the snapshot taken when the rep
                                // clicked the sidebar row, so any server-side field
                                // that changed afterwards stayed stale here. That
                                // left the archived-lead re-engagement banner
                                // (driven by thread.re_engaged_at) on screen with
                                // live buttons after it had already been resolved.
                                thread: {
                                    ...(whatsAppChatMetaDataClone?.thread ?? {}),
                                    ...threadData,
                                },
                            }
                            dispatch(slice.actions.refreshTagInfo({
                                whatsAppChatMetaData: {
                                    ...whatsAppChatMetaDataClone
                                }
                            }));
                        }
                        let updated;
                        if (res?.status === 200) {
                            if (refreshMessageDetails) {
                                // Move thread to top
                                const filtered = currentThreads.filter((t) => `${t.id}` !== `${threadData?.id ?? -1}`);
                                updated = [threadData, ...filtered];
                            } else {
                                // Update thread in-place
                                updated = currentThreads.map((t) =>
                                    t.id === threadData.id ? { ...t, ...threadData } : t
                                );
                            }
                            dispatch(slice.actions.saveThreadDetails(updated));
                        } else {
                            removeCurrentFromThreadAndUpdateInfo(currentThreads, threadId, refreshMessageDetails, whatsAppChatMetaDataClone);
                        }
                        if (options?.background) fetchFilterCountsInBackground();
                        else fetchFilterCounts(false);
                    })
                    .catch((error) => {
                        console.error("Error fetching thread:", error);
                    });
            }

            // Handle lead response independently
            if (leadPromise) {
                leadPromise
                    .then(async (res) => {
                        let leadData = res;
                        if (leadData?.status !== 200) {
                            leadData = await callApi(`/api/v1/practices/${practiceId}/archived/${leadId}/`, 'GET');
                        }
                        if (leadData?.status === 200) {
                            // Detect if backend moved lead to a new follow-up stage
                            const oldLead = whatsAppChatMetaData?.leadDetails;
                            const newLead = leadData?.data;
                            if (oldLead && newLead) {
                                try {
                                    const oldTreatments = oldLead?.lead_treatments ?? [];
                                    const newTreatments = newLead?.lead_treatments ?? [];
                                    const changedTreatment = newTreatments.find((newTreatment) => {
                                        const oldTreatment = oldTreatments.find(t => t.id === newTreatment.id);
                                        if (!oldTreatment) return false;
                                        const status = newTreatment?.status ?? oldTreatment?.status;
                                        const oldCount = oldTreatment?.lead_contacted_counts?.find(c => c.status === status)?.count ?? 0;
                                        const newCount = newTreatment?.lead_contacted_counts?.find(c => c.status === status)?.count ?? 0;
                                        return newCount > oldCount;
                                    });
                                    if (changedTreatment) {
                                        const status = changedTreatment?.status;
                                        const newCount = changedTreatment?.lead_contacted_counts?.find(c => c.status === status)?.count ?? 0;
                                        const leadName = `${newLead?.first_name ?? ''} ${newLead?.last_name ?? ''}`.trim() || 'Lead';
                                        const followUpLabel = newCount >= 3 ? 'Follow up 3+' : `Follow up ${newCount}`;
                                        dispatch(updateError({
                                            message: `${leadName} has been moved to ${followUpLabel}`,
                                            success: 'success',
                                            timeout: 5000,
                                        }));
                                    }
                                } catch (e) {
                                    console.error('Error checking follow-up status change:', e);
                                }
                            }

                            dispatch(
                                slice.actions.saveLeadDetails({
                                    ...whatsAppChatMetaData,
                                    leadDetails: leadData?.data ?? {},
                                })
                            );
                        } else {
                            dispatch(updateError({
                                message: "Please contact support! There is some discrepancy with the data with this lead",
                                success: false
                            }));
                        }
                    })
                    .catch((error) => {
                        console.error("Error fetching lead:", error);
                    });
            }
        } catch (error) {
            console.error("Unexpected error in refreshing thread and message info", error);
            dispatch(
                updateError({
                    message: "Failed to update chat! Please refresh",
                    success: "warning",
                })
            );
        }
    };
}

export function fetchMoreWhastappMessages() {
    return async () => {
        const state = store.getState()?.whatsappV2;
        const whatsAppChatNextAPI = state?.whatsAppChatNextAPI;
        const whatsappChats = state?.whatsappChats;
        try {
            dispatch(slice.actions.startWhatsappNextMessageFetch());
            const result = whatsAppChatNextAPI.match(/api\/v1.*/);
            const extractedPath = result ? result[0] : null;
            const threadResponse = await callApi(`/${extractedPath}`, 'GET');
            dispatch(slice.actions.completeWhatsappNextMessageFetch({
                whatsappChats: [...whatsappChats, ...(threadResponse?.data?.results ?? [])],
                whatsAppChatNextAPI: threadResponse?.data?.next
            }))
        } catch (error) {
            console.error("Error in finding next messages", error);
            dispatch(updateError({ message: "Failed to get older chats, please try after sometime", success: false }));
            dispatch(slice.actions.completeWhatsappNextMessageFetch({ whatsappChats, whatsAppChatNextAPI }))
        }
    }
}

// One mark_as_read request per thread at a time. Opening a thread, the
// metadata fetch and the realtime refresh all dispatch markAsRead, and
// production logs showed identical requests for one thread in the same
// second — doubled traffic, doubled 401 rows at token expiry, and two
// thread refreshes. Concurrent callers now join the in-flight request;
// the key is released in `finally` so a later call (or a retry after a
// failure) sends a fresh request.
const markAsReadInflight = new Map();

export function markAsRead(threadId, refresh = true) {
    return async () => {
        try {
            if (threadId) {
                const practiceId = getPracticeId();
                const currentBusinessId = store.getState()?.whatsappV2?.currentBusinessId;
                const currentWhatsappPhoneId = store.getState()?.whatsappV2?.currentWhatsappPhoneId;
                // Guard: Don't make API calls if WhatsApp account/phone IDs are not set
                if (!currentBusinessId || !currentWhatsappPhoneId) {
                    return;
                }
                const key = `${practiceId}|${currentBusinessId}|${currentWhatsappPhoneId}|${threadId}`;
                if (markAsReadInflight.has(key)) {
                    await markAsReadInflight.get(key);
                    return;
                }
                const request = (async () => {
                    try {
                        await callApi(
                            `/api/v1/practices/${practiceId}/whatsapp-business-accounts/${currentBusinessId}/whatsapp-phones/${currentWhatsappPhoneId}/whatsapp-message-threads/${threadId}/mark_as_read/?is_archived_included=true`,
                            'POST'
                        );
                        dispatch(refreshThreadAndMessageInfo(threadId, null, refresh));
                    } finally {
                        markAsReadInflight.delete(key);
                    }
                })();
                markAsReadInflight.set(key, request);
                await request;
            }
        } catch (error) {
            console.error("Error in marking as read", error);
        }
    }
}

export function markAsUnRead(threadId) {
    return async () => {
        try {
            if (threadId) {
                const practiceId = getPracticeId();
                const currentBusinessId = store.getState()?.whatsappV2?.currentBusinessId;
                const currentWhatsappPhoneId = store.getState()?.whatsappV2?.currentWhatsappPhoneId;

                // Guard: Don't make API calls if WhatsApp account/phone IDs are not set
                if (!currentBusinessId || !currentWhatsappPhoneId) {
                    return;
                }

                const response = await callApi(
                    `/api/v1/practices/${practiceId}/whatsapp-business-accounts/${currentBusinessId}/whatsapp-phones/${currentWhatsappPhoneId}/whatsapp-message-threads/${threadId}/mark_as_unread/?is_archived_included=true`,
                    'POST'
                );
                if (!response?.ok) {
                    // Tier 3 fixup: surface 4xx/5xx to the operator instead
                    // of silently succeeding the optimistic UI update.
                    dispatch(updateError({
                        success: false,
                        message: response?.data?.error || 'Could not mark as unread',
                    }));
                    return;
                }
                dispatch(refreshThreadAndMessageInfo(threadId));
            }
        } catch (error) {
            dispatch(updateError({ success: false, message: 'Could not mark as unread' }));
            console.error("Error in marking as unread", error);
        }
    }
}

export function addWhatsappTag(threadId, tagName, tagColor) {
    return async () => {
        try {
            if (threadId) {
                const practiceId = getPracticeId();
                const currentBusinessId = store.getState()?.whatsappV2?.currentBusinessId;
                const currentWhatsappPhoneId = store.getState()?.whatsappV2?.currentWhatsappPhoneId;

                // Guard: Don't make API calls if WhatsApp account/phone IDs are not set
                if (!currentBusinessId || !currentWhatsappPhoneId) {
                    return;
                }

                const whatsAppChatMetaData = store.getState()?.whatsappV2?.whatsAppChatMetaData;
                whatsAppChatMetaData?.tags?.forEach(tagObject => {
                    callApi(
                        `/api/v1/practices/${practiceId}/whatsapp-business-accounts/${currentBusinessId}/whatsapp-phones/${currentWhatsappPhoneId}/whatsapp-message-threads/${threadId}/untag/`,
                        'POST',
                        {
                            name: tagObject?.name,
                        }
                    )
                })
                await callApi(
                    `/api/v1/practices/${practiceId}/whatsapp-business-accounts/${currentBusinessId}/whatsapp-phones/${currentWhatsappPhoneId}/whatsapp-message-threads/${threadId}/tag/`,
                    'POST',
                    {
                        name: tagName,
                        colour: tagColor
                    }
                );
                dispatch(refreshThreadAndMessageInfo(threadId, null, true));
                dispatch(updateError({ message: "Successfully Added Tag to Whatsapp Thread", success: true }));
            }
        } catch (error) {
            dispatch(updateError({ message: "Failed to add tag to the thread", success: false }));
            console.error("Error in adding tags", error);
        }
    }
}

export function removeWhatsappTag(threadId) {
    return async () => {
        try {
            if (!threadId) return;

            const practiceId = getPracticeId();
            const state = store.getState()?.whatsappV2;
            const currentBusinessId = state?.currentBusinessId;
            const currentWhatsappPhoneId = state?.currentWhatsappPhoneId;

            // Guard: Don't make API calls if WhatsApp account/phone IDs are not set
            if (!hasValidWhatsappContext(currentBusinessId, currentWhatsappPhoneId)) {
                return;
            }

            const tags = state?.whatsAppChatMetaData?.tags ?? [];

            if (!Array.isArray(tags) || tags.length === 0) {
                dispatch(updateError({ message: "No tags to remove", success: true }));
                return;
            }

            const base = `/api/v1/practices/${practiceId}/whatsapp-business-accounts/${currentBusinessId}/whatsapp-phones/${currentWhatsappPhoneId}/whatsapp-message-threads/${threadId}`;

            // Fire untag calls in parallel and wait for all to settle
            const untagPromises = tags.map((tagObject) =>
                callApi(`${base}/untag/`, 'POST', { name: tagObject?.name })
            );

            const results = await Promise.allSettled(untagPromises);
            const failures = results.filter(r => r.status === 'rejected').length;

            // Ensure refresh only after all untag operations are complete
            await store.dispatch(refreshThreadAndMessageInfo(threadId, null, true));

            dispatch(updateError({
                message: failures ? `Removed tags with ${failures} failure(s)` : "Successfully Removed Tag(s)",
                success: failures === 0
            }));
        } catch (error) {
            dispatch(updateError({ message: "Failed to remove tag from the thread", success: false }));
            console.error("Error in removing tags", error);
        }
    }
}

/**
 * @param {boolean} suppressUndo ISS-025 — omit the Undo affordance. Dismissing
 * an archived-lead re-engagement also re-archives the thread on the backend, so
 * a reopen would not actually bring the conversation back; offering Undo there
 * would promise something the system cannot deliver.
 */
export function closeWhatsappConversationWithReason(threadId, reason, leadId, suppressUndo = false, opts = {}) {
    return async () => {
        try {
            if (!threadId) {
                captureWhatsappWorkflow('close_conversation_missing_thread', {
                    level: 'warning',
                    extra: { reason: 'missing_thread_id' },
                });
                dispatch(updateError({ message: 'Could not close conversation. Please refresh and try again.', success: false, status: 0 }));
                return { ok: false };
            }

            const practiceId = getPracticeId();
            const state = store.getState()?.whatsappV2;
            const currentBusinessId = state?.currentBusinessId;
            const currentWhatsappPhoneId = state?.currentWhatsappPhoneId;

            // Guard: Don't make API calls if WhatsApp account/phone IDs are not set
            if (!currentBusinessId || !currentWhatsappPhoneId) {
                captureWhatsappWorkflow('close_conversation_context_missing', {
                    level: 'warning',
                    extra: {
                        practiceId,
                        businessId: currentBusinessId,
                        phoneId: currentWhatsappPhoneId,
                        threadId,
                        reason: 'missing_whatsapp_context',
                    },
                });
                dispatch(updateError({ message: 'WhatsApp is still loading. Please try again in a moment.', success: false, status: 0 }));
                return { ok: false };
            }

            captureWhatsappWorkflow('close_conversation_api_dispatched', {
                extra: { practiceId, businessId: currentBusinessId, phoneId: currentWhatsappPhoneId, threadId },
            });
            const url = threadDetailUrl(practiceId, currentBusinessId, currentWhatsappPhoneId, threadId);
            const response = await callApi(
                url,
                'PATCH', {
                is_closed: true,
                metadata: {
                    close_reason: reason
                }
            }
            );
            if (response?.status === 200) {
                captureWhatsappWorkflow('close_conversation_api_success', {
                    extra: { practiceId, businessId: currentBusinessId, phoneId: currentWhatsappPhoneId, threadId, status: response?.status },
                });
                dispatch(updateError({
                    // ISS-025B — the "ignore this re-enquiry" flow reuses this
                    // thunk, because choosing not to action a re-enquiry IS a
                    // close as far as the backend is concerned. But
                    // "Conversation resolved" is the wrong sentence for it: the
                    // rep did not resolve anything, they hid an archived
                    // patient. Callers may supply the exact wording; every
                    // existing call site keeps the historical message unchanged.
                    message: opts?.successMessage ?? `Conversation resolved — ${reason ?? ""}`,
                    success: true,
                    timeout: 6000,
                    ...(suppressUndo
                        ? {}
                        : {
                            undo: {
                                type: 'reopen_whatsapp',
                                threadId,
                                leadId,
                            }
                        }),
                }));
                // Ensure refresh only after all untag operations are complete
                await store.dispatch(refreshThreadAndMessageInfo(threadId, null, true));
                return { ok: true };
            }
            // status 0 means NO RESPONSE ARRIVED — request aborted on navigation or
            // unmount, timed out, offline, or blocked by the path guard
            // (apiCall.js:235). It is not a server rejection and there is nothing
            // the user can do differently, so it must not be reported as a hard
            // failure. Same reasoning and same treatment as the thread-reconcile
            // branch above (ISS-016): warn to the console, keep the Sentry
            // breadcrumb at 'info' so the rate is still visible, and skip the toast.
            //
            // The reported event was exactly this: status 0 with
            // errorMessage 'non_200_response', logged at level 'error' and toasted
            // as "Failed to close whatsapp conversation. Please try again." — for a
            // request that never reached the server. The close may well have been
            // applied; telling staff it failed invites a duplicate action.
            if (response?.status === 0) {
                captureWhatsappWorkflow('close_conversation_no_response', {
                    level: 'info',
                    extra: {
                        practiceId,
                        businessId: currentBusinessId,
                        phoneId: currentWhatsappPhoneId,
                        threadId,
                    },
                });
                console.warn('[whatsappV2] close conversation got no response; skipping toast', { threadId });
                return { ok: false, noResponse: true };
            }
            const detail = response?.data?.detail || response?.data?.message || '';
            const msg = detail
                ? `Failed to close conversation: ${detail}`
                : 'Failed to close whatsapp conversation. Please try again.';
            captureWhatsappWorkflow('close_conversation_api_failure', {
                level: 'error',
                extra: {
                    practiceId,
                    businessId: currentBusinessId,
                    phoneId: currentWhatsappPhoneId,
                    threadId,
                    status: response?.status,
                    errorMessage: detail || response?.data?.error || 'non_200_response',
                },
            });
            dispatch(updateError({ message: msg, success: false, status: response?.status }));
            return { ok: false };
        } catch (error) {
            captureWhatsappWorkflow('close_conversation_exception', {
                level: 'error',
                extra: { threadId, errorMessage: error?.message || 'unknown_exception' },
            });
            dispatch(updateError({ message: "Failed to close whatsapp conversation. Please try again.", success: false, status: 0, cause: error }));
            console.error("Failed to close whatsapp conversation", error);
            return { ok: false };
        }
    }
}

/**
 * ISS-025B — the reason string persisted on `metadata.close_reason` when a rep
 * ignores an archived patient's re-enquiry. Exported so the Resolved tab and
 * any future reporting can recognise these closes without matching prose.
 */
export const IGNORE_RE_ENGAGEMENT_REASON = 'Ignored — patient stays archived';

/**
 * ISS-025B — "Ignore" an archived lead's re-enquiry.
 *
 * Semantics the rep is buying: the patient STAYS archived, this conversation
 * leaves the inbox, and it comes back only if they message again.
 *
 * Mechanically this is the existing close: the backend's `perform_update`
 * sees `is_closed=True` with a live `re_engaged_at`, calls
 * `clear_re_engagement(re_archive_thread=True)`, and the row then fails both
 * clauses of the `lead__is_archived=false` carve-out in
 * `apps/whatsapp/filters.py` — so it drops out of every tab and every counter.
 * A later inbound finds `re_engaged_at IS NULL`, re-runs `mark_re_engagement`,
 * and the conversation returns.
 *
 * Deliberately NOT routed through the lead: `Lead.archive()` is not idempotent
 * (`apps/utils/models.py` rewrites `archived_at = now()` on every call), and
 * this is the one flow that renders that timestamp back to the user.
 *
 * `suppressUndo` is hard-coded true — the toast's Undo reopens the thread, but
 * the thread is archived again by then, so a reopen would not bring the row
 * back and the affordance would lie.
 */
export function ignoreReEngagement(threadId, leadId, contactName) {
    return async () => {
        if (!threadId) {
            captureWhatsappWorkflow('ignore_re_engagement_missing_thread', {
                level: 'warning',
                extra: { reason: 'missing_thread_id' },
            });
            dispatch(updateError({ message: 'Could not hide this conversation. Please refresh and try again.', success: false, status: 0 }));
            return { ok: false };
        }
        const who = (typeof contactName === 'string' && contactName.trim()) || 'This patient';
        captureWhatsappWorkflow('ignore_re_engagement_dispatched', { extra: { threadId } });

        const result = await dispatch(
            closeWhatsappConversationWithReason(
                threadId,
                IGNORE_RE_ENGAGEMENT_REASON,
                leadId,
                true,
                {
                    successMessage:
                        `Hidden — ${who} stays archived. The chat comes back if they message again.`,
                },
            )
        );

        // Drop the row locally. The close thunk awaits
        // `refreshThreadAndMessageInfo(threadId, null, true)`, whose refresh
        // branch re-inserts the refetched thread at the TOP of the list — so
        // without this the conversation the rep just hid would be the first
        // thing they see until the next full list fetch. Ordering is load-
        // bearing: this must run after that await has resolved.
        if (result?.ok) {
            dispatch(slice.actions.removeThreadFromList(threadId));
        }
        // No fetchFilterCounts here: the close thunk's awaited
        // `refreshThreadAndMessageInfo` already refetches the counts, and it
        // does so AFTER the PATCH landed, so they are already correct. A second
        // call would just be a duplicate request per ignore.
        return result ?? { ok: false };
    };
}

export function reOpenWhatsappConversationWithReason(threadId, leadId) {
    return async () => {
        try {
            if (!threadId) {
                captureWhatsappWorkflow('reopen_conversation_missing_thread', {
                    level: 'warning',
                    extra: { reason: 'missing_thread_id' },
                });
                dispatch(updateError({ message: 'Could not reopen conversation. Please refresh and try again.', success: false, status: 0 }));
                return;
            }

            const practiceId = getPracticeId();
            const state = store.getState()?.whatsappV2;
            const currentBusinessId = state?.currentBusinessId;
            const currentWhatsappPhoneId = state?.currentWhatsappPhoneId;

            // Guard: Don't make API calls if WhatsApp account/phone IDs are not set
            if (!currentBusinessId || !currentWhatsappPhoneId) {
                captureWhatsappWorkflow('reopen_conversation_context_missing', {
                    level: 'warning',
                    extra: {
                        practiceId,
                        businessId: currentBusinessId,
                        phoneId: currentWhatsappPhoneId,
                        threadId,
                        reason: 'missing_whatsapp_context',
                    },
                });
                dispatch(updateError({ message: 'WhatsApp is still loading. Please try again in a moment.', success: false, status: 0 }));
                return;
            }

            captureWhatsappWorkflow('reopen_conversation_api_dispatched', {
                extra: { practiceId, businessId: currentBusinessId, phoneId: currentWhatsappPhoneId, threadId },
            });
            const response = await callApi(
                threadDetailUrl(practiceId, currentBusinessId, currentWhatsappPhoneId, threadId),
                'PATCH', {
                is_closed: false,
                metadata: {
                    closeReason: ""
                }
            }
            );
            if (response?.status === 200) {
                captureWhatsappWorkflow('reopen_conversation_api_success', {
                    extra: { practiceId, businessId: currentBusinessId, phoneId: currentWhatsappPhoneId, threadId, status: response?.status },
                });
                dispatch(updateError({
                    message: `Successfully reopened thread`,
                    success: true
                }));
                // Ensure refresh only after all untag operations are complete
                await store.dispatch(refreshThreadAndMessageInfo(threadId, null, true));
                const addNotesObj = {
                    note: "OTHER",
                    additional_notes: "Whatsapp Chat Reopened!",
                    lead: leadId
                };
                if (leadId)
                    await callApi(`/api/v1/practices/${practiceId}/leads/${leadId}/notes/`, 'post', addNotesObj);
            }
            else if (response?.status === 0) {
                // Same transport-abort case as the close path above: no response
                // arrived, so this is not a server rejection. Reopen is the UNDO for
                // close, so a false "failed to reopen" is worse than useless — it
                // pushes staff to retry an action that may already have applied.
                captureWhatsappWorkflow('reopen_conversation_no_response', {
                    level: 'info',
                    extra: {
                        practiceId,
                        businessId: currentBusinessId,
                        phoneId: currentWhatsappPhoneId,
                        threadId,
                    },
                });
                console.warn('[whatsappV2] reopen conversation got no response; skipping toast', { threadId });
            }
            else {
                const detail = response?.data?.detail || response?.data?.message || response?.data?.error || '';
                captureWhatsappWorkflow('reopen_conversation_api_failure', {
                    level: 'error',
                    extra: {
                        practiceId,
                        businessId: currentBusinessId,
                        phoneId: currentWhatsappPhoneId,
                        threadId,
                        status: response?.status,
                        errorMessage: detail || 'non_200_response',
                    },
                });
                dispatch(updateError({
                    message: detail ? `Failed to reopen conversation: ${detail}` : "Failed to reopen whatsapp conversation!",
                    success: false,
                    status: response?.status,
                }));
            }
        } catch (error) {
            captureWhatsappWorkflow('reopen_conversation_exception', {
                level: 'error',
                extra: { threadId, errorMessage: error?.message || 'unknown_exception' },
            });
            dispatch(updateError({ message: "Failed to reopen whatsapp conversation", success: false, status: 0, cause: error }));
            console.error("Failed to reopen whatsapp conversation", error);
        }
    }
}

export function reactToMessage(threadId, messageId, emoji) {
    return async () => {
        const practiceId = getPracticeId();
        const state = store.getState()?.whatsappV2;
        const currentBusinessId = state?.currentBusinessId;
        const currentWhatsappPhoneId = state?.currentWhatsappPhoneId;
        if (!practiceId || !threadId || !messageId || !hasConnectedWhatsappWriteContext(state)) return;

        const list = state?.whatsappChats ?? [];
        const previous = list.find((m) => String(m?.id) === String(messageId))?.whatsapp_message_reaction || null;

        const removed = !emoji;
        dispatch(applyMessageReaction({ messageId, emoji: removed ? null : emoji, removed }));

        try {
            const response = await callApi(
                `/api/v1/practices/${practiceId}/whatsapp-business-accounts/${currentBusinessId}/whatsapp-phones/${currentWhatsappPhoneId}/whatsapp-message-threads/${threadId}/whatsapp-messages/${messageId}/react/`,
                'POST',
                { emoji: removed ? '' : emoji },
            );
            if (!response?.ok) {
                dispatch(applyMessageReaction({
                    messageId,
                    emoji: previous?.emoji ?? null,
                    removed: !previous?.emoji,
                }));
                dispatch(updateError({
                    success: false,
                    message: response?.data?.error || 'Failed to send reaction',
                }));
            }
        } catch (error) {
            dispatch(applyMessageReaction({
                messageId,
                emoji: previous?.emoji ?? null,
                removed: !previous?.emoji,
            }));
            dispatch(updateError({ success: false, message: 'Failed to send reaction' }));
            console.error('Error sending reaction', error);
        }
    };
}

export function revokeMessage(threadId, messageId) {
    return async () => {
        const practiceId = getPracticeId();
        const state = store.getState()?.whatsappV2;
        const currentBusinessId = state?.currentBusinessId;
        const currentWhatsappPhoneId = state?.currentWhatsappPhoneId;
        if (!practiceId || !threadId || !messageId || !hasConnectedWhatsappWriteContext(state)) return;

        const list = state?.whatsappChats ?? [];
        const previous = list.find((m) => String(m?.id) === String(messageId));
        const previousBody = previous?.body ?? '';
        const previousIsRevoked = Boolean(previous?.is_revoked);

        dispatch(applyMessageRevoked({ messageId }));

        try {
            const response = await callApi(
                `/api/v1/practices/${practiceId}/whatsapp-business-accounts/${currentBusinessId}/whatsapp-phones/${currentWhatsappPhoneId}/whatsapp-message-threads/${threadId}/whatsapp-messages/${messageId}/revoke/`,
                'DELETE',
            );
            // ISS-017 — a 404 means the message is already gone server-side,
            // which is exactly what the user asked for. Reverting the optimistic
            // delete made it visibly reappear and then reported a failure. The
            // thread reconcile above (:2722) already treats 404 as "already
            // gone"; this path never got the same treatment. Keep it deleted.
            if (response?.status === 404) {
                console.warn('[whatsappV2] revoke returned 404; message already gone', { threadId, messageId });
            } else if (!response?.ok) {
                if (previous && !previousIsRevoked) {
                    dispatch(revertMessageRevoked({ messageId, body: previousBody }));
                }
                dispatch(updateError({
                    success: false,
                    message: response?.data?.error || 'Failed to delete message',
                }));
            }
        } catch (error) {
            if (previous && !previousIsRevoked) {
                dispatch(revertMessageRevoked({ messageId, body: previousBody }));
            }
            dispatch(updateError({ success: false, message: 'Failed to delete message' }));
            console.error('Error revoking message', error);
        }
    };
}

// ---------------------------------------------------------------------
// Track C — F6 Edit + F8 Message Info thunks
// ---------------------------------------------------------------------

// editMessage(threadId, messageId, newBody)
//
// Optimistic body swap → PATCH .../edit/ → on failure surgically revert.
// We capture the previous body BEFORE dispatching applyMessageEdited so the
// revert action can put it back exactly. The thunk does NOT toast on the
// success path because the PieSocket message-edited event is the
// source-of-truth UI update for any other operator viewing the same chat.
export function editMessage(threadId, messageId, newBody) {
    return async () => {
        try {
            const practiceId = getPracticeId();
            const state = store.getState()?.whatsappV2 ?? {};
            const { currentBusinessId, currentWhatsappPhoneId } = state;

            if (!practiceId || !hasConnectedWhatsappWriteContext(state)) {
                dispatch(updateError({
                    message: 'WhatsApp context not initialised — please retry.',
                    success: false,
                }));
                return;
            }

            // Snapshot the prior shape so a server failure can put the bubble
            // back exactly as it was. This is the surgical-revert pattern
            // Track A's fixup established for reactions.
            const messages = state.whatsappChats ?? [];
            const prior = messages.find((m) => `${m?.id}` === `${messageId}`);
            const previousBody = prior?.body ?? '';
            const previousIsEdited = prior?.is_edited ?? false;
            const previousEditedAt = prior?.edited_at ?? null;

            dispatch(slice.actions.applyMessageEdited({
                messageId,
                newBody,
                editedAt: new Date().toISOString(),
            }));

            const url = `/api/v1/practices/${practiceId}/whatsapp-business-accounts/${currentBusinessId}/whatsapp-phones/${currentWhatsappPhoneId}/whatsapp-message-threads/${threadId}/whatsapp-messages/${messageId}/edit/`;
            const res = await callApi(url, 'PATCH', { body: newBody });

            if (res?.status === 200) {
                // Server confirmed; PieSocket will fan out to every other
                // socket. No additional toast — silent success matches WA's
                // native edit affordance.
                return;
            }

            // Non-200: revert. Pull a friendly code/message from the payload.
            dispatch(slice.actions.revertMessageEdited({
                messageId,
                previousBody,
                previousIsEdited,
                previousEditedAt,
            }));
            const errorPayload = res?.data ?? {};
            const code = errorPayload?.code;
            const msg = errorPayload?.error || (
                code === 'edit_window_expired'
                    ? 'Edit window expired (15-minute Meta limit).'
                    : 'Failed to edit message.'
            );
            dispatch(updateError({ message: msg, success: false }));
        } catch (error) {
            console.error('Failed to edit whatsapp message', error);
            dispatch(updateError({ message: 'Failed to edit message.', success: false }));
        }
    };
}

// fetchMessageInfo(threadId, messageId)
//
// Lazy-fetched only when the operator opens the Info drawer. Backend never
// includes status_logs in the main message-list payload — that's intentional
// per D12 to keep chat-render cheap.
export function fetchMessageInfo(threadId, messageId) {
    return async () => {
        try {
            const practiceId = getPracticeId();
            const state = store.getState()?.whatsappV2 ?? {};
            const { currentBusinessId, currentWhatsappPhoneId } = state;
            if (!practiceId || !currentBusinessId || !currentWhatsappPhoneId) return;

            dispatch(slice.actions.startMessageInfoFetch(messageId));

            const url = `/api/v1/practices/${practiceId}/whatsapp-business-accounts/${currentBusinessId}/whatsapp-phones/${currentWhatsappPhoneId}/whatsapp-message-threads/${threadId}/whatsapp-messages/${messageId}/info/`;
            const res = await callApi(url, 'GET');
            if (res?.status === 200) {
                dispatch(slice.actions.completeMessageInfoFetch({
                    messageId,
                    info: {
                        sent_at: res?.data?.sent_at ?? null,
                        delivered_at: res?.data?.delivered_at ?? null,
                        read_at: res?.data?.read_at ?? null,
                        failed_at: res?.data?.failed_at ?? null,
                    },
                }));
            } else {
                dispatch(slice.actions.failMessageInfoFetch(messageId));
            }
        } catch (error) {
            console.error('Failed to fetch message info', error);
            dispatch(slice.actions.failMessageInfoFetch(messageId));
        }
    };
}

// AI Assistant thunk
export function generateAiResponse(actionType, context = {}) {
    return async () => {
        try {
            // Get AI config from state
            const state = store.getState();
            const aiConfig = state?.whatsappV2?.aiConfig || {};

            // Check if AI is enabled for WhatsApp
            if (aiConfig.enableAiForWhatsapp === false) {
                dispatch(slice.actions.completeAiAssistant({
                    content: 'AI assistance is currently disabled for WhatsApp. Please enable it in Practice Choices > AI Config.',
                    actionType: 'DISABLED',
                    isDisabled: true
                }));
                return;
            }

            dispatch(slice.actions.startAiAssistant(actionType));
            const practiceId = getPracticeId();

            // Build system prompt and messages based on action type, including AI config
            const { systemPrompt, messages } = buildAiPromptAndMessages(actionType, context, aiConfig);

            const response = await callApi(
                `/api/v1/practices/${practiceId}/ai/chat/`,
                'POST',
                {
                    system_prompt: systemPrompt,
                    messages
                },
                false, // isUnAuthorized
                { timeout: 60000 } // 60 second timeout for AI responses
            );

            if (response?.ok || response?.status === 200) {
                // Extract the AI response content from the API response
                const aiContent = response?.data?.choices?.[0]?.message?.content
                    || response?.data?.message?.content
                    || response?.data?.content
                    || response?.data?.response
                    || response?.data;

                dispatch(slice.actions.completeAiAssistant({
                    content: typeof aiContent === 'string' ? aiContent : JSON.stringify(aiContent),
                    actionType
                }));
            } else {
                const rawError = response?.data?.error || response?.data?.message || '';
                // Check for OpenRouter API key not configured error
                const isApiKeyError = rawError.toLowerCase().includes('openrouter') ||
                    rawError.toLowerCase().includes('api key not configured') ||
                    rawError.toLowerCase().includes('not configured');

                if (isApiKeyError) {
                    const infoMessage = 'AI is not configured for your practice. Please contact support to enable AI features.';
                    dispatch(slice.actions.completeAiAssistant({
                        content: infoMessage,
                        actionType: 'NOT_CONFIGURED',
                        isNotConfigured: true
                    }));
                    dispatch(updateError({
                        message: infoMessage,
                        success: 'warning'
                    }));
                } else {
                    const errorMessage = rawError || 'Failed to get AI response';
                    dispatch(slice.actions.failAiAssistant(errorMessage));
                    dispatch(updateError({
                        message: errorMessage,
                        success: false
                    }));
                }
            }
        } catch (error) {
            console.error("Error in AI Assistant", error);
            const rawError = error?.message || '';
            const isApiKeyError = rawError.toLowerCase().includes('openrouter') ||
                rawError.toLowerCase().includes('api key not configured') ||
                rawError.toLowerCase().includes('not configured');

            if (isApiKeyError) {
                const infoMessage = 'AI is not configured for your practice. Please contact support to enable AI features.';
                dispatch(slice.actions.completeAiAssistant({
                    content: infoMessage,
                    actionType: 'NOT_CONFIGURED',
                    isNotConfigured: true
                }));
                dispatch(updateError({
                    message: infoMessage,
                    success: 'warning'
                }));
            } else {
                const errorMessage = 'Failed to get AI response. Please try again.';
                dispatch(slice.actions.failAiAssistant(errorMessage));
                dispatch(updateError({
                    message: errorMessage,
                    success: false
                }));
            }
        }
    };
}

// Helper function to build AI prompts and messages
function buildAiPromptAndMessages(actionType, context, aiConfig = {}) {
    const { leadName, treatment, treatmentStatus, recentMessages, draftMessage, targetLanguage } = context;

    // Get the number of messages to use for context (from config, max 10)
    // Use slice(-messagesLimit) to get the LAST N messages (most recent), not the first N (oldest)
    const messagesLimit = Math.min(aiConfig.messagesPerNumberContext || 10, 10);
    const limitedMessages = recentMessages?.slice(-messagesLimit) || [];

    // Base context for all prompts
    const leadContext = leadName ? `Patient Name: ${leadName}` : '';
    const treatmentContext = treatment ? `Treatment: ${treatment}` : '';
    const statusContext = treatmentStatus ? `Treatment Status: ${treatmentStatus}` : '';
    const contextInfo = [leadContext, treatmentContext, statusContext].filter(Boolean).join('\n');

    // Format recent messages for context (limited by config)
    const conversationHistory = limitedMessages?.map(msg =>
        `${msg.role === 'patient' ? 'Patient' : 'Staff'}: ${msg.content}`
    ).join('\n') || '';

    // Build practice context from AI config
    const buildPracticeContext = () => {
        const sections = [];
        if (aiConfig.practiceInfo) {
            sections.push(`=== PRACTICE INFORMATION ===\n${aiConfig.practiceInfo}`);
        }
        if (aiConfig.treatmentInfo) {
            sections.push(`=== TREATMENT PROCESSES & STEPS ===\n${aiConfig.treatmentInfo}`);
        }
        if (aiConfig.pricingInfo) {
            sections.push(`=== PRICING INFORMATION ===\n${aiConfig.pricingInfo}`);
        }
        if (aiConfig.sopMessages) {
            sections.push(`=== STANDARD SOP MESSAGES ===\n${aiConfig.sopMessages}`);
        }
        if (aiConfig.communicationStyle) {
            sections.push(`=== COMMUNICATION STYLE GUIDELINES ===\n${aiConfig.communicationStyle}`);
        }
        return sections.length > 0 ? `\n\n--- PRACTICE KNOWLEDGE BASE ---\n${sections.join('\n\n')}` : '';
    };

    const practiceContext = buildPracticeContext();

    const prompts = {
        SMART_REPLY: {
            systemPrompt: `You are a helpful assistant for a dental practice. Generate a professional, friendly WhatsApp reply based on the conversation context.
${contextInfo ? `\nPatient Context:\n${contextInfo}` : ''}${practiceContext}

Guidelines:
- Keep responses concise and suitable for WhatsApp (under 300 characters when possible)
- Be warm, professional, and helpful
- Use the patient's name if available
- Address their specific concerns or questions
- Use the practice knowledge base to provide accurate information
- Do not use markdown formatting`,
            userMessage: `Based on this conversation, generate an appropriate reply:\n\n${conversationHistory}\n\n${draftMessage ? `Draft intent: ${draftMessage}` : 'Generate a helpful response to the last message.'}`
        },
        APPOINTMENT_REMINDER: {
            systemPrompt: `You are a helpful assistant for a dental practice. Generate a friendly appointment reminder message.
${contextInfo ? `\nPatient Context:\n${contextInfo}` : ''}${practiceContext}

Guidelines:
- Keep it brief and friendly
- Include treatment type if known
- Remind them to arrive 10 minutes early
- Follow the communication style from practice guidelines
- Do not use markdown formatting`,
            userMessage: `Generate a WhatsApp appointment reminder for this patient. ${conversationHistory ? `\n\nRecent conversation:\n${conversationHistory}` : ''}`
        },
        FOLLOW_UP: {
            systemPrompt: `You are a helpful assistant for a dental practice. Generate a professional follow-up message for a patient who hasn't responded.
${contextInfo ? `\nPatient Context:\n${contextInfo}` : ''}${practiceContext}

Guidelines:
- Be polite and not pushy
- Reference previous conversation if relevant
- Offer to help or answer questions
- Use SOP messages as reference where applicable
- Do not use markdown formatting`,
            userMessage: `Generate a follow-up message for this patient.\n\nRecent conversation:\n${conversationHistory}`
        },
        HANDLE_CONCERN: {
            systemPrompt: `You are a helpful assistant for a dental practice. Generate an empathetic response to a patient's concern (pricing, scheduling, anxiety, etc.).
${contextInfo ? `\nPatient Context:\n${contextInfo}` : ''}${practiceContext}

Guidelines:
- Be understanding and empathetic
- Address their specific concern
- Offer solutions or alternatives when possible
- Use practice pricing info and SOP messages as reference
- Reassure them without being dismissive
- Do not use markdown formatting`,
            userMessage: `Generate a compassionate response to this patient's concern:\n\n${conversationHistory}`
        },
        SUMMARIZE_CHAT: {
            systemPrompt: `You are a helpful assistant. Summarize this WhatsApp conversation from a dental practice.
${contextInfo ? `\nPatient Context:\n${contextInfo}` : ''}

Provide:
- Key discussion points
- Any decisions made
- Current status of the conversation
- Keep it concise (bullet points preferred)`,
            userMessage: `Summarize this conversation:\n\n${conversationHistory}`
        },
        PAIN_POINTS: {
            systemPrompt: `You are a helpful assistant. Extract and summarize the patient's pain points, concerns, and needs from this dental practice conversation.
${contextInfo ? `\nPatient Context:\n${contextInfo}` : ''}

Identify:
- Main concerns or complaints
- Hesitations or objections
- Questions they need answered
- Emotional state or anxiety indicators`,
            userMessage: `Extract the patient's pain points from this conversation:\n\n${conversationHistory}`
        },
        ACTION_ITEMS: {
            systemPrompt: `You are a helpful assistant. Extract action items and next steps from this dental practice conversation.
${contextInfo ? `\nPatient Context:\n${contextInfo}` : ''}

List:
- Tasks for staff to complete
- Follow-ups needed
- Patient commitments mentioned
- Deadlines or timeframes mentioned`,
            userMessage: `Extract action items from this conversation:\n\n${conversationHistory}`
        },
        EXPLAIN_TREATMENT: {
            systemPrompt: `You are a helpful assistant for a dental practice. Generate a patient-friendly explanation of a dental treatment.
${contextInfo ? `\nPatient Context:\n${contextInfo}` : ''}${practiceContext}

Guidelines:
- Use simple, non-technical language
- Explain benefits clearly
- Use the treatment information from the practice knowledge base
- Address common concerns
- Keep it reassuring and informative
- Do not use markdown formatting`,
            userMessage: `Generate a patient-friendly explanation of ${treatment || 'the dental treatment they inquired about'}.\n\n${conversationHistory ? `Context from conversation:\n${conversationHistory}` : ''}`
        },
        PRICING_INFO: {
            systemPrompt: `You are a helpful assistant for a dental practice. Generate a professional message about pricing and payment options.
${contextInfo ? `\nPatient Context:\n${contextInfo}` : ''}${practiceContext}

Guidelines:
- Be transparent and professional
- Use the pricing information from the practice knowledge base when available
- Mention financing options if applicable
- Invite them to discuss further or book a consultation for exact pricing
- Do not use markdown formatting`,
            userMessage: `Generate a message about pricing for ${treatment || 'dental services'}.\n\n${conversationHistory ? `Context:\n${conversationHistory}` : ''}`
        },
        IMPROVE_TONE: {
            systemPrompt: `You are a helpful assistant. Improve the tone of this message to be more professional and friendly while keeping the same meaning.
${aiConfig.communicationStyle ? `\nCommunication Style Guidelines:\n${aiConfig.communicationStyle}` : ''}

Guidelines:
- Maintain the original intent
- Make it warmer and more professional
- Follow the communication style guidelines if provided
- Fix any grammar issues
- Keep appropriate length
- Do not use markdown formatting`,
            // Use draftMessage if provided, otherwise get the LAST message (most recent) from the conversation
            userMessage: `Improve the tone of this message:\n\n${draftMessage || limitedMessages?.slice(-1)?.[0]?.content || 'No message to improve'}`
        },
        TRANSLATE: {
            systemPrompt: `You are a translation assistant. Translate the message to ${targetLanguage || 'English'}.

Guidelines:
- Maintain the tone and intent
- Use natural phrasing in the target language
- Keep formatting simple for WhatsApp`,
            // Use draftMessage if provided, otherwise get the LAST message (most recent) from the conversation
            userMessage: `Translate this to ${targetLanguage || 'English'}:\n\n${draftMessage || limitedMessages?.slice(-1)?.[0]?.content || 'No message to translate'}`
        }
    };

    const promptConfig = prompts[actionType] || prompts.SMART_REPLY;

    return {
        systemPrompt: promptConfig.systemPrompt,
        messages: [
            { role: 'user', content: promptConfig.userMessage }
        ]
    };
}

// Star + Pin thunks (Track B). All thunks pull threadId from the caller
// and pull practiceId / business / phone from store.getState() so the
// caller doesn't have to thread three more args through.

const buildMessageUrl = (threadId, messageId, suffix) => {
    const practiceId = getPracticeId();
    const state = store.getState()?.whatsappV2;
    const businessId = state?.currentBusinessId;
    const phoneId = state?.currentWhatsappPhoneId;
    if (!practiceId || !threadId || !messageId || !businessId || !phoneId) return null;
    return `/api/v1/practices/${practiceId}/whatsapp-business-accounts/${businessId}/whatsapp-phones/${phoneId}/whatsapp-message-threads/${threadId}/whatsapp-messages/${messageId}/${suffix}/`;
};

const buildThreadUrl = (threadId, suffix) => {
    const practiceId = getPracticeId();
    const state = store.getState()?.whatsappV2;
    const businessId = state?.currentBusinessId;
    const phoneId = state?.currentWhatsappPhoneId;
    if (!practiceId || !threadId || !businessId || !phoneId) return null;
    return `/api/v1/practices/${practiceId}/whatsapp-business-accounts/${businessId}/whatsapp-phones/${phoneId}/whatsapp-message-threads/${threadId}/${suffix}/`;
};

export function starMessage(threadId, messageId) {
    return async () => {
        const url = buildMessageUrl(threadId, messageId, 'star');
        if (!url) return;

        const list = store.getState()?.whatsappV2?.whatsappChats ?? [];
        const previous = list.find((m) => String(m?.id) === String(messageId));
        const previouslyStarred = Boolean(previous?.is_starred);

        dispatch(applyMessageStarred({ messageId }));

        try {
            const response = await callApi(url, 'POST', {});
            if (!response?.ok) {
                if (!previouslyStarred) {
                    dispatch(applyMessageUnstarred({ messageId }));
                }
                dispatch(updateError({
                    success: false,
                    message: response?.data?.error || 'Failed to star message',
                }));
            } else if (response?.data?.starred_by) {
                dispatch(applyMessageStarred({ messageId, starredBy: response.data.starred_by }));
            }
        } catch (error) {
            if (!previouslyStarred) {
                dispatch(applyMessageUnstarred({ messageId }));
            }
            dispatch(updateError({ success: false, message: 'Failed to star message' }));
            console.error('Error starring message', error);
        }
    };
}

export function unstarMessage(threadId, messageId) {
    return async () => {
        const url = buildMessageUrl(threadId, messageId, 'star');
        if (!url) return;

        const list = store.getState()?.whatsappV2?.whatsappChats ?? [];
        const previous = list.find((m) => String(m?.id) === String(messageId));
        const previousStarredBy = previous?.starred_by ?? null;
        const previouslyStarred = Boolean(previous?.is_starred);

        dispatch(applyMessageUnstarred({ messageId }));

        try {
            const response = await callApi(url, 'DELETE');
            if (!response?.ok) {
                if (previouslyStarred) {
                    dispatch(applyMessageStarred({ messageId, starredBy: previousStarredBy }));
                }
                dispatch(updateError({
                    success: false,
                    message: response?.data?.error || 'Failed to unstar message',
                }));
            }
        } catch (error) {
            if (previouslyStarred) {
                dispatch(applyMessageStarred({ messageId, starredBy: previousStarredBy }));
            }
            dispatch(updateError({ success: false, message: 'Failed to unstar message' }));
            console.error('Error unstarring message', error);
        }
    };
}

export function pinMessage(threadId, messageId) {
    return async () => {
        const url = buildThreadUrl(threadId, 'pin');
        if (!url || !messageId) return;

        const meta = store.getState()?.whatsappV2?.whatsAppChatMetaData ?? {};
        const previousPinned = meta?.thread?.pinned_message ?? null;
        const previousPinnedAt = meta?.thread?.pinned_at ?? null;

        // Optimistic — replace the banner before the network round-trip so
        // the operator sees instant feedback. Surgical revert on error
        // restores the previous pin (or no pin).
        const list = store.getState()?.whatsappV2?.whatsappChats ?? [];
        const target = list.find((m) => String(m?.id) === String(messageId));
        dispatch(applyMessagePinned({
            messageId,
            threadId,
            pinnedMessage: target
                ? { id: target.id, body: target.content ?? target.body ?? '', from_waid: target.fromWaid ?? target.from_waid, created_time: target.created_time }
                : { id: messageId },
            pinnedAt: new Date().toISOString(),
        }));

        try {
            const response = await callApi(url, 'POST', { message_id: messageId });
            if (!response?.ok) {
                if (previousPinned) {
                    dispatch(applyMessagePinned({
                        messageId: previousPinned.id,
                        threadId,
                        pinnedMessage: previousPinned,
                        pinnedAt: previousPinnedAt,
                    }));
                } else {
                    dispatch(applyMessageUnpinned({ threadId }));
                }
                dispatch(updateError({
                    success: false,
                    message: response?.data?.error || 'Failed to pin message',
                }));
            } else if (response?.data?.pinned_message) {
                dispatch(applyMessagePinned({
                    messageId: response.data.pinned_message.id,
                    threadId,
                    pinnedMessage: response.data.pinned_message,
                    pinnedAt: response.data.pinned_at,
                }));
            }
        } catch (error) {
            if (previousPinned) {
                dispatch(applyMessagePinned({
                    messageId: previousPinned.id,
                    threadId,
                    pinnedMessage: previousPinned,
                    pinnedAt: previousPinnedAt,
                }));
            } else {
                dispatch(applyMessageUnpinned({ threadId }));
            }
            dispatch(updateError({ success: false, message: 'Failed to pin message' }));
            console.error('Error pinning message', error);
        }
    };
}

export function unpinMessage(threadId) {
    return async () => {
        const url = buildThreadUrl(threadId, 'pin');
        if (!url) return;

        const meta = store.getState()?.whatsappV2?.whatsAppChatMetaData ?? {};
        const previousPinned = meta?.thread?.pinned_message ?? null;
        const previousPinnedAt = meta?.thread?.pinned_at ?? null;

        dispatch(applyMessageUnpinned({ threadId }));

        try {
            const response = await callApi(url, 'DELETE');
            if (!response?.ok) {
                if (previousPinned) {
                    dispatch(applyMessagePinned({
                        messageId: previousPinned.id,
                        threadId,
                        pinnedMessage: previousPinned,
                        pinnedAt: previousPinnedAt,
                    }));
                }
                dispatch(updateError({
                    success: false,
                    message: response?.data?.error || 'Failed to unpin message',
                }));
            }
        } catch (error) {
            if (previousPinned) {
                dispatch(applyMessagePinned({
                    messageId: previousPinned.id,
                    threadId,
                    pinnedMessage: previousPinned,
                    pinnedAt: previousPinnedAt,
                }));
            }
            dispatch(updateError({ success: false, message: 'Failed to unpin message' }));
            console.error('Error unpinning message', error);
        }
    };
}

export function fetchStarredMessages(threadId) {
    return async () => {
        const url = buildThreadUrl(threadId, 'starred-messages');
        if (!url) return [];

        try {
            const response = await callApi(url, 'GET');
            if (response?.ok) {
                return response?.data?.results ?? [];
            }
            dispatch(updateError({
                success: false,
                message: response?.data?.error || 'Failed to load starred messages',
            }));
            return [];
        } catch (error) {
            dispatch(updateError({ success: false, message: 'Failed to load starred messages' }));
            console.error('Error fetching starred messages', error);
            return [];
        }
    };
}

// ---------------------------------------------------------------------
// Bulk thread actions (Track WA-bulk-archive-FE)
//
// These four thunks back the SidebarBulkActionBar. The BE endpoints all
// share a contract:
//   POST .../whatsapp-message-threads/<action>/
//   Body:    { thread_ids: [int, ...] }
//   200 OK:  { succeeded: [int], failed: [{ id, reason }] }
//   4xx:     permission denied or bad input
//
// Common patterns:
// - Each thunk returns the response so the caller can show an Undo
//   snackbar with the succeeded ids.
// - On non-OK, we surface a single error toast via `updateError` and
//   return `null` so the caller knows the operation failed.
// - On partial-success (succeeded.length > 0 && failed.length > 0) we
//   show a warning toast ("X succeeded, Y failed") so the operator
//   isn't blindsided by a quiet partial. Failed ids stay selected so
//   the operator can retry.
// - Optimistic updates run BEFORE the network round-trip ONLY when we
//   have very high confidence (mark_as_read / mark_as_unread). For
//   archive we wait for the response so we can correctly highlight
//   only the succeeded subset.
// ---------------------------------------------------------------------

const buildBulkThreadUrl = (action) => {
    const practiceId = getPracticeId();
    const state = store.getState()?.whatsappV2;
    const currentBusinessId = state?.currentBusinessId;
    const currentWhatsappPhoneId = state?.currentWhatsappPhoneId;
    if (!practiceId || !hasValidWhatsappContext(currentBusinessId, currentWhatsappPhoneId)) {
        return null;
    }
    return `/api/v1/practices/${practiceId}/whatsapp-business-accounts/${currentBusinessId}/whatsapp-phones/${currentWhatsappPhoneId}/whatsapp-message-threads/${action}/`;
};

const buildThreadDetailUrl = (threadId) => {
    const practiceId = getPracticeId();
    const state = store.getState()?.whatsappV2;
    const currentBusinessId = state?.currentBusinessId;
    const currentWhatsappPhoneId = state?.currentWhatsappPhoneId;
    if (!threadId || !practiceId || !hasValidWhatsappContext(currentBusinessId, currentWhatsappPhoneId)) {
        return null;
    }
    return `/api/v1/practices/${practiceId}/whatsapp-business-accounts/${currentBusinessId}/whatsapp-phones/${currentWhatsappPhoneId}/whatsapp-message-threads/${threadId}/?is_archived_included=true`;
};

const surfacePartialFailure = (response) => {
    const failed = response?.data?.failed ?? [];
    const succeeded = response?.data?.succeeded ?? [];
    if (failed.length > 0) {
        // Warning, not error — the succeeded ones did go through.
        dispatch(updateError({
            success: 'warning',
            message: `${succeeded.length} succeeded, ${failed.length} failed`,
            timeout: 5000,
        }));
    }
};

export function bulkResolveThreads(threadIds, reason = 'Resolved') {
    return async () => {
        const ids = Array.isArray(threadIds) ? threadIds : [];
        if (ids.length === 0) return null;
        const state = store.getState()?.whatsappV2;
        try {
            captureWhatsappWorkflow('bulk_resolve_api_dispatched', {
                extra: {
                    practiceId: getPracticeId(),
                    businessId: state?.currentBusinessId,
                    phoneId: state?.currentWhatsappPhoneId,
                    selectedCount: ids.length,
                    reason,
                },
            });

            const results = await Promise.allSettled(ids.map(async (threadId) => {
                const url = buildThreadDetailUrl(threadId);
                if (!url) throw new Error('missing_whatsapp_context');
                const response = await callApi(url, 'PATCH', {
                    is_closed: true,
                    metadata: { close_reason: reason },
                });
                if (!response?.ok) {
                    throw new Error(response?.data?.detail || response?.data?.error || `Failed to resolve ${threadId}`);
                }
                return threadId;
            }));

            const succeeded = [];
            const failed = [];
            results.forEach((result, index) => {
                const threadId = ids[index];
                if (result.status === 'fulfilled') succeeded.push(threadId);
                else failed.push({ id: threadId, reason: result.reason?.message || 'Failed to resolve conversation' });
            });

            if (succeeded.length > 0) {
                dispatch(slice.actions.removeThreadsFromList(succeeded));
                fetchFilterCounts(false);
            }

            const responseShape = { data: { succeeded, failed } };
            captureWhatsappWorkflow(failed.length > 0 ? 'bulk_resolve_api_partial_failure' : 'bulk_resolve_api_success', {
                level: failed.length > 0 ? 'warning' : 'info',
                extra: {
                    practiceId: getPracticeId(),
                    businessId: state?.currentBusinessId,
                    phoneId: state?.currentWhatsappPhoneId,
                    selectedCount: ids.length,
                    succeededCount: succeeded.length,
                    failedCount: failed.length,
                    reason,
                },
            });
            surfacePartialFailure(responseShape);
            if (succeeded.length === 0 && failed.length > 0) {
                dispatch(updateError({ success: false, message: 'Failed to resolve conversations' }));
            }
            return { succeeded, failed };
        } catch (error) {
            captureWhatsappWorkflow('bulk_resolve_exception', {
                level: 'error',
                extra: {
                    selectedCount: ids.length,
                    errorMessage: error?.message || 'unknown_exception',
                },
            });
            console.error('bulkResolveThreads error', error);
            dispatch(updateError({ success: false, message: 'Failed to resolve conversations', status: 0, cause: error }));
            return null;
        }
    };
}

export function bulkReopenResolvedThreads(threadIds) {
    return async () => {
        const ids = Array.isArray(threadIds) ? threadIds : [];
        if (ids.length === 0) return null;
        try {
            const results = await Promise.allSettled(ids.map(async (threadId) => {
                const url = buildThreadDetailUrl(threadId);
                if (!url) throw new Error('missing_whatsapp_context');
                const response = await callApi(url, 'PATCH', {
                    is_closed: false,
                    metadata: { close_reason: '' },
                });
                if (!response?.ok) {
                    throw new Error(response?.data?.detail || response?.data?.error || `Failed to reopen ${threadId}`);
                }
                return threadId;
            }));
            const succeeded = [];
            const failed = [];
            results.forEach((result, index) => {
                const threadId = ids[index];
                if (result.status === 'fulfilled') succeeded.push(threadId);
                else failed.push({ id: threadId, reason: result.reason?.message || 'Failed to reopen conversation' });
            });
            dispatch(getWhatsaAppThreads());
            fetchFilterCounts(false);
            return { succeeded, failed };
        } catch (error) {
            console.error('bulkReopenResolvedThreads error', error);
            dispatch(updateError({ success: false, message: 'Failed to reopen conversations' }));
            return null;
        }
    };
}

export function bulkArchiveThreads(threadIds) {
    return async () => {
        const ids = Array.isArray(threadIds) ? threadIds : [];
        if (ids.length === 0) return null;
        try {
            // Hyphenated URL — the backend `@action(url_path="bulk-archive")`
            // is authoritative. DRF does NOT translate underscores to
            // hyphens, so `bulk_archive` would 404 on every request.
            const url = buildBulkThreadUrl('bulk-archive');
            const state = store.getState()?.whatsappV2;
            if (!url) {
                captureWhatsappWorkflow('bulk_archive_context_missing', {
                    level: 'warning',
                    extra: {
                        practiceId: getPracticeId(),
                        businessId: state?.currentBusinessId,
                        phoneId: state?.currentWhatsappPhoneId,
                        selectedCount: ids.length,
                        reason: 'missing_whatsapp_context',
                    },
                });
                dispatch(updateError({ success: false, message: 'WhatsApp is still loading. Please try again in a moment.', status: 0 }));
                return null;
            }
            captureWhatsappWorkflow('bulk_archive_api_dispatched', {
                extra: {
                    practiceId: getPracticeId(),
                    businessId: state?.currentBusinessId,
                    phoneId: state?.currentWhatsappPhoneId,
                    selectedCount: ids.length,
                },
            });
            const response = await callApi(url, 'POST', { thread_ids: ids });
            if (!response?.ok) {
                captureWhatsappWorkflow('bulk_archive_api_failure', {
                    level: 'error',
                    extra: {
                        practiceId: getPracticeId(),
                        businessId: state?.currentBusinessId,
                        phoneId: state?.currentWhatsappPhoneId,
                        selectedCount: ids.length,
                        status: response?.status,
                        errorMessage: response?.data?.detail || response?.data?.error || 'non_ok_response',
                    },
                });
                dispatch(updateError({
                    success: false,
                    message: response?.data?.detail || response?.data?.error || 'Failed to archive conversations',
                    status: response?.status,
                }));
                return null;
            }
            const succeeded = response?.data?.succeeded ?? [];
            const failed = response?.data?.failed ?? [];
            if (succeeded.length > 0) {
                dispatch(slice.actions.removeThreadsFromList(succeeded));
                setTimeout(() => {
                    try {
                        const visibleIds = new Set((store.getState()?.whatsappV2?.threadMessages || []).map((thread) => thread?.id));
                        const stillVisibleCount = succeeded.filter((id) => visibleIds.has(id)).length;
                        if (stillVisibleCount > 0) {
                            captureWhatsappWorkflow('bulk_archive_completion_not_observed', {
                                level: 'warning',
                                extra: {
                                    practiceId: getPracticeId(),
                                    businessId: state?.currentBusinessId,
                                    phoneId: state?.currentWhatsappPhoneId,
                                    selectedCount: ids.length,
                                    succeededCount: succeeded.length,
                                    failedCount: stillVisibleCount,
                                    reason: 'archived_threads_still_visible',
                                },
                            });
                        }
                    } catch (_) {
                        // Best-effort completion check only.
                    }
                }, 3000);
            }
            captureWhatsappWorkflow(failed.length > 0 ? 'bulk_archive_api_partial_failure' : 'bulk_archive_api_success', {
                level: failed.length > 0 ? 'warning' : 'info',
                extra: {
                    practiceId: getPracticeId(),
                    businessId: state?.currentBusinessId,
                    phoneId: state?.currentWhatsappPhoneId,
                    selectedCount: ids.length,
                    succeededCount: succeeded.length,
                    failedCount: failed.length,
                    status: response?.status,
                },
            });
            surfacePartialFailure(response);
            return response?.data ?? { succeeded, failed: [] };
        } catch (error) {
            captureWhatsappWorkflow('bulk_archive_exception', {
                level: 'error',
                extra: {
                    selectedCount: ids.length,
                    errorMessage: error?.message || 'unknown_exception',
                },
            });
            console.error('bulkArchiveThreads error', error);
            dispatch(updateError({ success: false, message: 'Failed to archive conversations', status: 0, cause: error }));
            return null;
        }
    };
}

export function bulkUnarchiveThreads(threadIds) {
    return async () => {
        const ids = Array.isArray(threadIds) ? threadIds : [];
        if (ids.length === 0) return null;
        try {
            const url = buildBulkThreadUrl('bulk-unarchive');
            if (!url) return null;
            const response = await callApi(url, 'POST', { thread_ids: ids });
            if (!response?.ok) {
                dispatch(updateError({
                    success: false,
                    message: response?.data?.detail || response?.data?.error || 'Failed to restore conversations',
                }));
                return null;
            }
            // Refetch the list — the unarchived threads need to come
            // back into view (they were filtered out when archived).
            dispatch(getWhatsaAppThreads());
            surfacePartialFailure(response);
            return response?.data ?? null;
        } catch (error) {
            console.error('bulkUnarchiveThreads error', error);
            dispatch(updateError({ success: false, message: 'Failed to restore conversations' }));
            return null;
        }
    };
}

export function bulkMarkThreadsRead(threadIds) {
    return async () => {
        const ids = Array.isArray(threadIds) ? threadIds : [];
        if (ids.length === 0) return null;
        try {
            const url = buildBulkThreadUrl('bulk-mark-as-read');
            if (!url) return null;
            const response = await callApi(url, 'POST', { thread_ids: ids });
            if (!response?.ok) {
                dispatch(updateError({
                    success: false,
                    message: response?.data?.detail || response?.data?.error || 'Failed to mark as read',
                }));
                return null;
            }
            const succeeded = response?.data?.succeeded ?? ids;
            dispatch(slice.actions.applyThreadsRead(succeeded));
            // Refresh the filter counts (unread badge in the nav).
            fetchFilterCounts(false);
            surfacePartialFailure(response);
            return response?.data ?? { succeeded, failed: [] };
        } catch (error) {
            console.error('bulkMarkThreadsRead error', error);
            dispatch(updateError({ success: false, message: 'Failed to mark as read' }));
            return null;
        }
    };
}

export function bulkMarkThreadsUnread(threadIds) {
    return async () => {
        const ids = Array.isArray(threadIds) ? threadIds : [];
        if (ids.length === 0) return null;
        try {
            const url = buildBulkThreadUrl('bulk-mark-as-unread');
            if (!url) return null;
            const response = await callApi(url, 'POST', { thread_ids: ids });
            if (!response?.ok) {
                dispatch(updateError({
                    success: false,
                    message: response?.data?.detail || response?.data?.error || 'Failed to mark as unread',
                }));
                return null;
            }
            const succeeded = response?.data?.succeeded ?? ids;
            dispatch(slice.actions.applyThreadsUnread(succeeded));
            fetchFilterCounts(false);
            surfacePartialFailure(response);
            return response?.data ?? { succeeded, failed: [] };
        } catch (error) {
            console.error('bulkMarkThreadsUnread error', error);
            dispatch(updateError({ success: false, message: 'Failed to mark as unread' }));
            return null;
        }
    };
}
